import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { cn } from '@/lib/utils';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, X, Phone, FileText, Calculator, Plus, Minus, MessageSquare, RotateCcw, LayoutGrid, Hammer } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { getProjectsForLocale } from '../data/projects';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
import { useT, useLocale } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { SmallLoadBreakdown } from './SmallLoadBreakdown';
import { useCart } from './CartContext';
import { WelcomeBackCard } from './WelcomeBackCard';
import { PersonalGreeting, usePersonalGreeting } from './PersonalGreeting';
import { AddressAutocomplete } from './AddressAutocomplete';
import { getRegionMeta } from '../utils/region';
import { getDefaultQuantity, getSmallLoadSurcharge, buildDeliveryLineItems } from '../utils/loadSplit';
import { getRecentAddresses, pushRecentAddress } from '../utils/recentOrders';
// Live ticker — rotates through recent activity. Uses an animated truck + sliding text instead of a blinking dot to avoid clashing with the green pulse in the trust badge above.
function LiveDeliveryTicker({
  isDark = false,
  onPhoto = false
}: {
  isDark?: boolean;
  onPhoto?: boolean;
}) {
  // These are ORDERS placed (web/phone), not delivery times. We constrain
  // the displayed window tightly to business hours so the feed always looks
  // like it came from the dispatch desk — no 9pm or 2am orders.
  // Active window: 6am – 6pm, with peak weighting toward 8am–5pm.
  // Sundays are quiet, so on Sunday most displayed orders pull from
  // Saturday and Friday.
  const ACTIVE_START_HOUR = 6; // 6:00 AM — earliest displayed order time
  const ACTIVE_END_HOUR = 18; // 6:00 PM — latest displayed order time
  const ACTIVE_MINUTES = (ACTIVE_END_HOUR - ACTIVE_START_HOUR) * 60; // 720
  // "Today progress" uses the dispatch-style 6am–5pm window for
  // weighting how many orders are from today vs prior days.
  const PROGRESS_END_HOUR = 17; // 5:00 PM
  const PROGRESS_MINUTES = (PROGRESS_END_HOUR - ACTIVE_START_HOUR) * 60; // 660
  // Live "now" — updates every 60s so on-screen timestamps tick forward
  // ("3 min ago" → "4 min ago") even if the user lingers on the page.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const formatRelative = (target: Date, now: Date) => {
    const diffMin = Math.max(1, Math.round((now.getTime() - target.getTime()) / 60000));
    const sameDay = target.toDateString() === now.toDateString();
    // Only show "X min ago" / "X hr ago" during business hours
    // (Mon–Sat 6am–6pm). Outside that window — including Sat 6pm
    // through Mon 6am — fall through to the dated time format so
    // the feed doesn't fake fresh minute-by-minute activity.
    const dow = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const hour = now.getHours();
    const inBusinessHours = dow >= 1 && dow <= 6 && hour >= 6 && hour < 18;
    if (inBusinessHours && sameDay && diffMin < 60) return `${diffMin} min ago`;
    if (inBusinessHours && sameDay && diffMin < 8 * 60) return `${Math.round(diffMin / 60)} hr ago`;
    const timeStr = target.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    if (sameDay) return `Today ${timeStr}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.toDateString() === yesterday.toDateString()) return `Yesterday ${timeStr}`;
    return `${target.toLocaleDateString('en-US', {
      weekday: 'short'
    })} ${timeStr}`;
  };
  // Static order info — only the timestamp varies based on the current time.
  // Distribution matches real sales: ~75% of orders are the four top sellers
  // (5/8" Minus, 3-Way Topsoil, Drain Rock, Bark). The remaining 25% is a
  // realistic mix of other materials.
  const regionCities = getRegionMeta().cities;
  const getCity = (index: number, fallback: string) => {
    if (index % 4 !== 0 && regionCities.length > 0) {
      return regionCities[index % regionCities.length];
    }
    return fallback;
  };
  const baseActivities = [
  // 5/8" Minus — top seller (3 entries)
  {
    material: '5/8" Minus',
    qty: 13,
    city: getCity(0, 'Tacoma')
  }, {
    material: '5/8" Minus',
    qty: 10,
    city: getCity(1, 'Kent')
  }, {
    material: '5/8" Minus',
    qty: 14,
    city: getCity(2, 'Bellevue')
  },
  // 3-Way Topsoil — top seller (3 entries)
  {
    material: '3-Way Topsoil',
    qty: 7,
    city: getCity(3, 'Seattle')
  }, {
    material: '3-Way Topsoil',
    qty: 5,
    city: getCity(4, 'Puyallup')
  }, {
    material: '3-Way Topsoil',
    qty: 12,
    city: getCity(5, 'Auburn')
  },
  // Drain Rock — top seller (3 entries)
  {
    material: '7/8" Drain Rock',
    qty: 12,
    city: getCity(6, 'Federal Way')
  }, {
    material: '1.5" Drain Rock',
    qty: 10,
    city: getCity(7, 'Renton')
  }, {
    material: '7/8" Drain Rock',
    qty: 8,
    city: getCity(8, 'Tacoma')
  },
  // Bark — top seller (3 entries)
  {
    material: 'Medium Bark',
    qty: 14,
    city: getCity(9, 'Sammamish')
  }, {
    material: 'Coarse Bark',
    qty: 15,
    city: getCity(10, 'Lakewood')
  }, {
    material: 'Fine Bark',
    qty: 12,
    city: getCity(11, 'Kirkland')
  },
  // Other materials (~25%)
  {
    material: '1.25" Minus',
    qty: 12,
    city: getCity(12, 'Sumner')
  }, {
    material: 'Pea Gravel',
    qty: 6,
    city: getCity(13, 'Issaquah')
  }, {
    material: 'Recycled Concrete',
    qty: 9,
    city: getCity(14, 'Bonney Lake')
  }, {
    material: '5/8" Clear',
    qty: 8,
    city: getCity(15, 'Redmond')
  },
  // Occasional long-haul deliveries beyond Zone 5 — shows we go further
  {
    material: '5/8" Minus',
    qty: 14,
    city: getCity(16, 'Olympia')
  }, {
    material: '3-Way Topsoil',
    qty: 13,
    city: getCity(17, 'Everett')
  }];
  const activities = useMemo(() => {
    // Snapshot "now" once when the activities list is built. The activity
    // list itself is stable for this page session — only the rendered
    // relative-time strings update via the live `now` tick above.
    const built = new Date();
    const total = baseActivities.length;
    const dow = built.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const isSunday = dow === 0;
    // Per-page-load shuffle of material/qty across city slots. Different
    // every refresh so two visits in a row don't show the exact same feed.
    // Cities stay in their slot positions so geographic distribution is
    // preserved — only the material/qty pairing rotates.
    const shuffledIdx = baseActivities.map((_, i) => i);
    for (let i = shuffledIdx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIdx[i], shuffledIdx[j]] = [shuffledIdx[j], shuffledIdx[i]];
    }
    const shuffledActivities = baseActivities.map((entry, i) => ({
      material: baseActivities[shuffledIdx[i]].material,
      qty: baseActivities[shuffledIdx[i]].qty,
      city: entry.city
    }));
    // Today's active window [5am → 10pm]
    const todayStart = new Date(built);
    todayStart.setHours(ACTIVE_START_HOUR, 0, 0, 0);
    const progressEnd = new Date(built);
    progressEnd.setHours(PROGRESS_END_HOUR, 0, 0, 0);
    // How far into the working day are we? 0 = before 5am, 1 = after 5pm.
    let progress = 0;
    if (built >= todayStart && built <= progressEnd) {
      progress = (built.getTime() - todayStart.getTime()) / (PROGRESS_MINUTES * 60000);
    } else if (built > progressEnd) {
      progress = 1;
    }
    const beforeWork = built < todayStart;
    // How many entries should be from "today"?
    // - Sunday: cap heavily — people aren't ordering as much. Max 2.
    // - Other days: scale with progress, but reserve at least 2 slots
    //   for prior-day orders so the feed doesn't feel one-day flat.
    let todayCount = 0;
    if (beforeWork) {
      todayCount = 0;
    } else if (isSunday) {
      todayCount = Math.min(2, Math.round(progress * 3));
    } else {
      todayCount = Math.min(total - 2, Math.round(progress * total));
    }
    // Remaining slots split across prior days.
    // - Sunday → Saturday + Friday (60/40)
    // - Monday → Sunday + Saturday (Sun fewer, so 30/70 toward Saturday)
    // - Other → almost all from yesterday, with 1 slot bleeding into 2 days ago
    const remaining = total - todayCount;
    const priorDays: {
      daysAgo: number;
      count: number;
    }[] = [];
    if (isSunday) {
      const satCount = Math.round(remaining * 0.6);
      priorDays.push({
        daysAgo: 1,
        count: satCount
      }); // Saturday
      priorDays.push({
        daysAgo: 2,
        count: remaining - satCount
      }); // Friday
    } else if (dow === 1) {
      // Monday — Sunday was quiet
      const sunCount = Math.round(remaining * 0.3);
      priorDays.push({
        daysAgo: 1,
        count: sunCount
      }); // Sunday
      priorDays.push({
        daysAgo: 2,
        count: remaining - sunCount
      }); // Saturday
    } else {
      // Most of remaining = yesterday, last one from 2 days ago for variety
      const olderCount = remaining > 4 ? 1 : 0;
      priorDays.push({
        daysAgo: 1,
        count: remaining - olderCount
      });
      if (olderCount > 0) priorDays.push({
        daysAgo: 2,
        count: olderCount
      });
    }
    type Stamped = (typeof baseActivities)[number] & {
      ts: Date;
    };
    const stamped: Stamped[] = [];
    let activityIdx = 0;
    // Helper: pick a "natural" order time within a given day, strictly
    // within business hours (6am–6pm) with peak density 8am–5pm.
    // No late-night stragglers — every order looks like it was placed
    // during dispatch hours.
    const buildPriorDayTime = (daysAgo: number) => {
      const day = new Date(built);
      day.setDate(day.getDate() - daysAgo);
      // Random minute within 6am–6pm, biased toward 8am–5pm (middle 80%).
      const frac = 0.1 + Math.random() * 0.8;
      const minutesFromStart = Math.round(frac * ACTIVE_MINUTES);
      day.setHours(ACTIVE_START_HOUR, 0, 0, 0);
      day.setMinutes(minutesFromStart);
      return day;
    };
    // TODAY orders — generate randomized "minutes ago" offsets so two
    // page loads in a row don't show identical timestamps. Newest entry
    // is always within the last 1–9 minutes (feels fresh). Subsequent
    // entries spread randomly across the elapsed work day.
    const todayMinutesElapsed = Math.max(1, Math.min(ACTIVE_MINUTES, Math.round((built.getTime() - todayStart.getTime()) / 60000)));
    const todayOffsets: number[] = [];
    if (todayCount > 0) {
      // Newest order: 1–9 min ago.
      todayOffsets.push(1 + Math.floor(Math.random() * 9));
      // Remaining: random spread across the rest of the elapsed window.
      const tail = Math.max(15, todayMinutesElapsed);
      for (let i = 1; i < todayCount; i++) {
        // Uniform over [10 min, todayMinutesElapsed], with light bias toward
        // the most recent half.
        const u = Math.random() ** 0.85;
        todayOffsets.push(10 + Math.round(u * (tail - 10)));
      }
      todayOffsets.sort((a, b) => a - b);
    }
    for (let i = 0; i < todayCount; i++) {
      const ts = new Date(built);
      ts.setMinutes(ts.getMinutes() - todayOffsets[i]);
      stamped.push({
        ...shuffledActivities[activityIdx % total],
        ts
      });
      activityIdx++;
    }
    // PRIOR-DAY orders — random "natural" times within each prior day.
    for (const pd of priorDays) {
      for (let i = 0; i < pd.count; i++) {
        const ts = buildPriorDayTime(pd.daysAgo);
        stamped.push({
          ...shuffledActivities[activityIdx % total],
          ts
        });
        activityIdx++;
      }
    }
    // Sort by recency, newest first.
    stamped.sort((a, b) => b.ts.getTime() - a.ts.getTime());
    return stamped;
  }, []);
  // Continuous right-to-left marquee of recent orders. The full order list
  // is rendered twice back-to-back and the track is translated -50% on an
  // infinite linear loop, so the feed scrolls seamlessly without a visible
  // reset. The relative timestamps still tick forward via the `now` state.
  const marqueeItems = [...activities, ...activities];
  // Slow, dispatch-desk pace — scales with how many orders are in the feed.
  const marqueeDuration = Math.max(80, activities.length * 8);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.6,
    duration: 0.8
  }} className="mt-9 flex items-center justify-center md:justify-start" data-id="element-858" data-mp="2gcjkhyk9syhq">
      <div style={{
      backgroundColor: onPhoto ? 'rgba(255,255,255,0.08)' : isDark ? 'rgba(255,255,255,0.06)' :
      // Light hero backdrop is now #e9f1e7; keep the ticker pill one
      // proportional shade darker than it so it still reads as a
      // distinct surface on the matched background.
      '#f4e3d3'
    }} className={cn('inline-flex items-center gap-3 pl-4 pr-3 py-1.5 border-l-2 border-[#1A824D] rounded-r-lg max-w-full overflow-hidden', (onPhoto || isDark) && 'backdrop-blur-md')} data-id="element-859" data-mp="3rokh1zndlr81">
        <div className="flex items-center gap-2 shrink-0" data-id="element-860" data-mp="xdlpbha8y6tg">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A824D]" data-id="element-861" data-mp="scxwqr6ddhx4">
            Live
          </span>
          <div className="flex items-end gap-0.5 h-3" data-id="element-862" data-mp="nemo2jrt9dgh">
            {[0, 1, 2].map(i => <motion.span key={i} className="w-0.5 bg-[#1A824D] rounded-full" animate={{
            height: ['30%', '100%', '30%']
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }} data-id="element-863" data-mp="ig32ikh4q3on" />)}
          </div>
        </div>
        <span className="text-stone-500 dark:text-white/30 shrink-0" data-id="element-864" data-mp="2ku9zbm0be80s">·</span>
        {/* Marquee viewport — content scrolls right→left continuously */}
        <div className="relative overflow-hidden w-[230px] sm:w-[420px]" data-id="element-865" data-mp="2avt4gdy9999g">
          <motion.div className="flex items-center w-max whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: marqueeDuration,
          ease: 'linear',
          repeat: Infinity
        }} aria-label="Recent orders" data-id="element-866" data-mp="2axnj5aixo553">
            {marqueeItems.map((item, i) => <span key={i} className="inline-flex items-center text-xs font-medium shrink-0" data-id="element-867" data-mp="26vsga1lctjp7" data-mpi={i}>
                <span className="font-[600] font-[Cabin,_sans-serif] text-[#1A824D] text-[12px]" data-id="element-868" data-mp="1s2yr1oga5p7c">
                  {item.qty} yds {item.material}
                </span>
                <span className="text-[#1A824D]" data-id="element-869" data-mp="1j5ofe1zjvy1w"> → </span>
                <span className="text-[#1A824D]" data-id="element-870" data-mp="1n5qdk162u9cf">{item.city}</span>
                <span className="text-[#1A824D]" data-id="element-871" data-mp="1n82ss6kga5ls">
                  {' · '}
                  {formatRelative(item.ts, now)}
                </span>
                <span className="mx-3 text-[#1A824D]/40" data-id="element-872" data-mp="zjd47v54ccot">•</span>
              </span>)}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}
// Build a flat product map and a grouped option list for the dropdown.
// Categories become disabled headers; each product is a selectable option
// so users can pick the exact material they need (and we can quote an
// exact per-yard price instead of a category range).
const categoryColors: Record<string, {
  headerBg: string;
  headerText: string;
  rowAccent: string;
  // Darkened variants for the green dark-mode dropdown — same hue family
  // as the light chips, deepened so they sit naturally on the Starbucks
  // green surface while preserving each category's color identity.
  darkHeaderBg: string;
  darkHeaderText: string;
}> = {
  'Base & Compaction': {
    headerBg: 'bg-red-100',
    headerText: 'text-red-800',
    rowAccent: 'border-l-[#BD3A3A]',
    darkHeaderBg: 'bg-red-950/70',
    darkHeaderText: 'text-red-300'
  },
  'Clear & Drain': {
    headerBg: 'bg-sky-100',
    headerText: 'text-sky-800',
    rowAccent: 'border-l-sky-400',
    darkHeaderBg: 'bg-sky-950/70',
    darkHeaderText: 'text-sky-300'
  },
  'Soil, Mulch & Org.': {
    headerBg: 'bg-emerald-100',
    headerText: 'text-emerald-800',
    rowAccent: 'border-l-emerald-400',
    darkHeaderBg: 'bg-emerald-950/70',
    darkHeaderText: 'text-emerald-300'
  },
  'Landscape & Decor': {
    headerBg: 'bg-amber-100',
    headerText: 'text-amber-800',
    rowAccent: 'border-l-amber-400',
    darkHeaderBg: 'bg-amber-950/70',
    darkHeaderText: 'text-amber-300'
  },
  'Quarry Rock': {
    headerBg: 'bg-slate-200',
    headerText: 'text-slate-700',
    rowAccent: 'border-l-slate-400',
    darkHeaderBg: 'bg-slate-900/80',
    darkHeaderText: 'text-slate-300'
  }
};
type ProductInfo = {
  pricePerYard: number;
  slug: string;
  categoryName: string;
};
const productMap: Record<string, ProductInfo> = {};
const materialOptions: {
  value: string;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
  categoryName?: string;
}[] = [];
categories.forEach(cat => {
  materialOptions.push({
    value: `__cat__${cat.name}`,
    label: cat.name,
    disabled: true,
    isHeader: true,
    categoryName: cat.name
  });
  cat.products.forEach(p => {
    productMap[p.name] = {
      pricePerYard: p.pricePerYard,
      slug: p.slug,
      categoryName: cat.name
    };
    materialOptions.push({
      value: p.name,
      label: `${p.name} — $${p.pricePerYard}/yd`,
      categoryName: cat.name
    });
  });
});
// Lightweight materials (bark, compost, soil, play chips) fit more cubic
// yards per truckload than dense aggregates. Used for full-load sizing
// and small-load surcharge thresholds.
//
// 50/50 Mix is the explicit exception inside the Soil, Mulch & Org.
// category — it's a heavier blend (sand + topsoil) that ships in a
// 12-yd truckload like rock and aggregates, not the 15-yd light load.
const isLightweight = (productName: string) => {
  if (!productName) return false;
  if (productName.toLowerCase().includes('50/50')) return false;
  return productMap[productName]?.categoryName === 'Soil, Mulch & Org.';
};
// Zone colors — must match the delivery zone map on /delivery-zones so a
// customer who jumps to the map sees the same color for their zone.
// Source of truth: pages/DeliveryZones.tsx → ZONE_COLORS.
// Zone 1 uses a soft lavender label; zones 2 and 3 keep their original
// yellow/red so the farther zones stay visually distinct.
const ZONE_COLORS: Record<number, string> = {
  1: '#00899B',
  2: '#FDAC53',
  3: '#DD4132' // Fiesta — outer ring (farthest)
};
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $57/yd).
// Computed dynamically from data/products.ts below so future price changes
// don't require touching this comment again.
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = [
// Removed — gravel-driveway hero photo (was the first entry in the rotation).
// Removed — hacienda/asphalt-roller photo; moved to the How It Works
// section background instead.
// Removed — construction-excavation-pit photo (was entry #3).
// Removed — was photo #3 in the rotation.
'https://cdn.magicpatterns.com/uploads/5JUUQPB3Bh6caPw5U1TsPF/grunge_dirt.jpg',
// Removed — was entry index 4.
'https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png'];
// Live deliveries counter — base 900 on a fixed anchor date, then adds
// a deterministic 10–20 deliveries per business day, plus partial progress
// for the current working day. Updates every minute.
// Counter reset to 5,560 as of 2026-06-10 — it counts up naturally from
// here (~10–20 deliveries per business day, plus partial progress today).
const DELIVERIES_ANCHOR = new Date('2026-06-10T00:00:00');
const DELIVERIES_BASE = 5560;
function seededRand(seed: number) {
  // Simple deterministic hash → [0,1)
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6; // Mon-Sat
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 9 * 60; // Sat 6-3
  return 11 * 60; // Mon-Fri 6-5
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 6 * 60) return 0;
    if (minutes >= 15 * 60) return 9 * 60;
    return minutes - 6 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  // 10–20 inclusive, deterministic per date
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  // Partial progress through today
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) {
      total += Math.floor(todayTotal * (elapsed / fullDay));
    }
  }
  return total;
}
function useLiveDeliveries() {
  const [count, setCount] = useState(() => computeDeliveriesCount(new Date()));
  useEffect(() => {
    const update = () => setCount(computeDeliveriesCount(new Date()));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);
  return count;
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
import { getZoneForPosition } from '../data/deliveryZones';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee,
    label: result.zone > 0 ? `${result.price} Delivery` : 'Custom Quote'
  };
}
function ShopByProjectCTA({
  isDarkText = false,
  estimator = false,
  isDark = false
}: {
  isDarkText?: boolean;
  estimator?: boolean;
  isDark?: boolean;
}) {
  // DARK mode keeps the reference-photo treatment: a SOLID green primary
  // ("Start your project" style — filled green, white text) and a DARK
  // charcoal outlined secondary ("Request a demo" style — near-black fill,
  // subtle border, white text).
  //
  // LIGHT mode reverts to the ORIGINAL estimator design: soft tinted-green
  // pills under the engaged estimator, and translucent glass pills over the
  // hero photo otherwise — driven off the `estimator` / `isDarkText` flags
  // exactly as before.
  const primaryTheme = isDark ? 'bg-[#006239] border-[#006239] text-white shadow-sm hover:bg-[#057345] hover:border-[#0C8A4C]' : estimator ? 'bg-[#059F40]/[0.10] border-[#059F40]/50 text-[#059F40] shadow-sm hover:border-[#059F40] hover:bg-[#059F40]/[0.16]' : isDarkText ? 'bg-stone-900/5 border-stone-900/15 text-stone-700 hover:bg-stone-900/10 hover:text-[#059F40]' :
  // Over the hero photo: solid brand green so the primary CTA is clearly
  // legible against the busy image instead of a near-invisible glass pill.
  'bg-[#006239] border-[#006239] text-white shadow-lg hover:bg-[#057345] hover:border-[#0C8A4C]';
  const secondaryTheme = isDark ? 'bg-[#1E1E1E] border-[#3A3A3A] text-white hover:bg-[#2A2A2A] hover:border-[#4A4A4A]' : estimator ? 'bg-white/55 border-[#059F40]/50 text-[#059F40] shadow-sm hover:border-[#059F40] hover:bg-white/70' : isDarkText ? 'bg-stone-900/5 border-stone-900/15 text-stone-700 hover:bg-stone-900/10 hover:text-[#059F40]' :
  // Over the hero photo: a solid dark charcoal pill with a visible border
  // and full-white text — clearly readable against the photo while still
  // reading as the secondary action next to the solid-green primary.
  'bg-black/55 border-white/40 text-white shadow-lg hover:bg-black/70 hover:border-white/60';
  const t = useT();
  const {
    locale
  } = useLocale();
  // Locale-aware project list — names and taglines flip with the active
  // language, slugs stay constant so the destination route is unchanged.
  const projects = getProjectsForLocale(locale);
  // First project's slug — clicking "Shop by project" deep-links into the
  // projects hub with the first project pre-opened. The project page
  // shows the full set of tabs across the top so users can switch from
  // there. Dedupe in case the data file holds locale duplicates.
  const firstProjectSlug = Array.from(new Map(projects.map(p => [p.slug, p])).values())[0]?.slug || 'new-gravel-driveway';
  const handleCatalogClick = () => {
    const el = document.getElementById('materials');
    if (el) {
      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };
  return <div className="flex flex-col items-center md:items-start w-full gap-3" data-id="element-873" data-mp="2ale805b4am64">
      {/* Side-by-side CTAs at 16px — "Shop by project" first as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 guided path that deep-links into the projects hub, then a thin
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 vertical separator, then the catalog link as the secondary action. */}
      <div className="flex flex-row items-stretch gap-2.5 w-full" data-id="element-874" data-mp="38uzxv7l3wa3t">
        <Link to={`/projects/${firstProjectSlug}`} className={cn('group flex flex-1 min-w-0 items-center justify-center gap-2 border font-semibold backdrop-blur-md transition-colors whitespace-nowrap', estimator ? 'px-4 py-3 text-sm rounded-full' : 'px-3.5 py-2.5 text-sm rounded-full', primaryTheme)} data-id="element-875" data-mp="3ml3gloks1loi">
          <Hammer className="w-4 h-4 shrink-0" data-id="element-876" data-mp="3rky5jmkzs16x" />
          {t('hero.estimator.shopByProject')}
          <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-877" data-mp="9ejsdpujrv9l" />
        </Link>

        <button type="button" onClick={handleCatalogClick} className={cn('group flex flex-1 min-w-0 items-center justify-center gap-2 border font-semibold backdrop-blur-md transition-colors whitespace-nowrap', estimator ? 'px-4 py-3 text-sm rounded-full' : 'px-3.5 py-2.5 text-sm rounded-full', secondaryTheme)} data-id="element-878" data-mp="5cyh28xnvp1i">
          <LayoutGrid className="w-4 h-4 shrink-0" data-id="element-879" data-mp="1x1xvcmfha4xf" />
          {t('hero.estimator.browseFullCatalog')}
          <ArrowRight className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-880" data-mp="210yfc89ozdm0" />
        </button>
      </div>
    </div>;
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  isDark = false
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    isHeader?: boolean;
    categoryName?: string;
  }[];
  placeholder: string;
  className?: string;
  isDark?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // When the dropdown opens, lift the page so the trigger is just
  // below the sticky nav. This guarantees the user always sees the
  // top of the dropdown after opening it, regardless of where the
  // estimator card was sitting in the viewport.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      const card = dropdownRef.current?.closest('.estimator-card') as HTMLElement | null;
      if (card) {
        const navHeight = 72; // top sticky 253GRAVEL nav, approx.
        const targetY = window.scrollY + card.getBoundingClientRect().top - navHeight;
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      } else {
        dropdownRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-881" data-mp="1l32p305r7isr">
      <button ref={buttonRef} type="button" onClick={() => setIsOpen(!isOpen)} style={{
      // Frosted surface matching the "recent orders" bar: a translucent
      // white fill over the dark card. No green active border anymore —
      // the OPEN (active) state is expressed by lifting the surface one
      // shade brighter instead, so the box brightens rather than gaining
      // a green outline. The hairline border stays neutral in every state.
      backgroundColor: isDark ? isOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)' :
      // Light mode: solid white, matching the address & quantity boxes.
      '#ffffff',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: isDark ? 'rgba(255,255,255,0.125)' : '#d6d3d1',
      backdropFilter: isDark ? 'none' : undefined,
      WebkitBackdropFilter: isDark ? 'none' : undefined
    }} className={cn(`w-full px-6 h-[68px] sm:h-[80px] rounded-[20px] text-xl sm:text-2xl font-medium focus:outline-none transition-colors flex items-center justify-between`, isDark ? value ? 'text-white' : 'text-white/60' : value ? 'text-stone-900' : 'text-stone-900/60')} data-id="element-882" data-mp="1h2ispyt08j7o">
        <span className="truncate" data-id="element-883" data-mp="gt0yg5ykvm0c">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-6 h-6 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-884" data-mp="244rkc89cf1b" />
      </button>

      <AnimatePresence data-id="element-885" data-mp="3pb75hmcqgg62">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className={cn('absolute z-50 w-full border-0 rounded-none shadow-xl overflow-y-scroll overscroll-contain touch-pan-y pb-[30px]', isDark ? 'estimator-dropdown-surface' : 'bg-white/55 backdrop-blur-md')} style={{
        // Fixed 350px height per spec. Internal scroll engages
        // for option lists taller than this; the pb-[30px] on
        // the container gives the user 30px of breathing room
        // past the last item when scrolled to the bottom.
        height: '300px',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-886" data-mp="3pbyn1t9hwddc">
            {options.map(opt => {
          const colors = opt.categoryName ? categoryColors[opt.categoryName] : null;
          if (opt.isHeader) {
            // Category headers — the per-category color labels are
            // preserved in BOTH modes. Light mode keeps the original soft
            // tinted chip; dark mode uses a deepened version of the same
            // hue so the color identity reads against the green surface.
            return <div key={opt.value} className={isDark ? `px-6 py-2.5 text-sm font-extrabold uppercase tracking-[0.14em] ${colors?.darkHeaderBg ?? 'bg-black/30'} ${colors?.darkHeaderText ?? 'text-white/80'} border-t border-white/10 first:border-t-0 select-none` : `px-6 py-2.5 text-sm font-extrabold uppercase tracking-[0.14em] ${colors?.headerBg ?? 'bg-stone-100'} ${colors?.headerText ?? 'text-stone-700'} border-t border-stone-200 first:border-t-0 select-none`} data-id="element-887" data-mp="6a07oq4p2ags">
                    {opt.label}
                  </div>;
          }
          return <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
            if (!opt.disabled) {
              onChange(opt.value);
              setIsOpen(false);
            }
          }} className={`w-full text-left px-6 py-3.5 text-lg transition-colors ${opt.disabled ? isDark ? 'text-white/30 cursor-not-allowed' : 'text-stone-300 cursor-not-allowed bg-stone-50' : value === opt.value ? isDark ? 'bg-white/15 text-white font-semibold' : 'bg-[#059F40]/20 text-[#059F40] font-semibold' : isDark ? 'text-white hover:bg-white/10' : `text-stone-900 hover:bg-stone-50 border-l-4 ${colors ? colors.rowAccent : 'border-l-transparent'}`}`} data-id="element-888" data-mp="1cs00rhwpepyd">
                  {opt.label}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  // The hero <section> root. After the address is confirmed and the material
  // row fires, we slide the page so the BOTTOM edge of the hero lines up with
  // the bottom edge of the (mobile) viewport, bringing the full estimator flow
  // into view.
  const heroSectionRef = useRef<HTMLElement>(null);
  // The live order ticker bar at the bottom of the hero. On estimator
  // activation we slide the page so this bar sits 40px above the bottom
  // edge of the (mobile) viewport.
  const tickerRef = useRef<HTMLDivElement>(null);
  // Slide so the live ticker's bottom edge sits 40px above the viewport
  // bottom. Falls back to the estimator card if the ticker isn't measured.
  const slideToTicker = () => {
    // Mobile-only — desktop keeps the hero/estimator in place beside the copy.
    if (window.innerWidth >= 768) return;
    const measure = () => {
      // Use the visual viewport height so the mobile keyboard dismissing
      // (which shrinks the visual viewport but NOT window.innerHeight)
      // doesn't land the ticker mid-screen.
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const ticker = tickerRef.current;
      if (ticker) {
        const rect = ticker.getBoundingClientRect();
        // Slide so the ticker's bottom edge sits ~24px above the viewport
        // bottom — bringing the live order ticker fully into view.
        const top = window.scrollY + rect.bottom - vh + 24;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      } else if (estimatorRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 80;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    };
    // Fire next frame, then re-measure once the keyboard has fully dismissed.
    requestAnimationFrame(measure);
    window.setTimeout(measure, 280);
  };
  // Slide so the BOTTOM edge of the estimator lines up with the bottom edge of
  // the viewport — used after a material is selected, so the customer sees the
  // whole estimator (address → material → quantity → price → Add to Truck)
  // sitting flush against the bottom of the phone.
  const slideEstimatorBottomToViewport = () => {
    // Mobile-only behavior — on desktop the estimator already sits fully in
    // view beside the hero copy, so we don't yank the page around.
    if (window.innerWidth >= 768) return;
    const card = estimatorRef.current;
    if (!card) {
      slideToTicker();
      return;
    }
    // Use the visual viewport height when available so we measure against the
    // ACTUAL visible area. The mobile keyboard, when up, shrinks the visual
    // viewport but NOT window.innerHeight — measuring innerHeight while the
    // keyboard was dismissing landed the estimator mid-screen. A small bottom
    // gap keeps the current last step off the very edge of the screen.
    const measure = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const BOTTOM_GAP = 12;
      const rect = card.getBoundingClientRect();
      const top = window.scrollY + rect.bottom - vh + BOTTOM_GAP;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
    };
    // Fire on the next frame (lets layout settle), then RE-measure ~280ms
    // later to correct for the mobile keyboard dismissing / address pill
    // collapsing, which changes both the viewport height and the card's
    // bottom edge AFTER the first measurement.
    requestAnimationFrame(measure);
    window.setTimeout(measure, 280);
  };
  const {
    addToCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // Recent delivery addresses this device has actually used — surfaced as
  // "recent" suggestions when the customer focuses the empty address box.
  // Sourced from the standalone recent-address history (written the moment an
  // address is confirmed in the estimator) AND backfilled from prior orders.
  // The tick lets us re-read after we persist a freshly-confirmed address so
  // it shows up next time without a full reload.
  const [recentAddrTick, setRecentAddrTick] = useState(0);
  const recentAddresses = useMemo(() => {
    try {
      return getRecentAddresses().slice(0, 4);
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentAddrTick]);
  // Track the active site theme (the `.dark` class the toggle adds to <html>)
  // so the engaged estimator's surfaces follow light/dark with the site.
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => obs.disconnect();
  }, []);
  // Rotating reassurance message above the estimator — swaps every 3s
  // between "Pay on delivery" trust copy and "30 min from your driveway"
  // proximity copy. Folds the freestanding green pill below the estimator
  // into a single high-attention spot at the top.
  const [rotatingMsgIndex, setRotatingMsgIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setRotatingMsgIndex(i => (i + 1) % 2);
    }, 3000);
    return () => clearInterval(id);
  }, []);
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('');
  // Most orders are full loads — preselect 12yd so users only need to confirm.
  // Switches automatically to 15 below if a lightweight material is chosen.
  const [quantity, setQuantity] = useState('12');
  const [numLoads, setNumLoads] = useState('');
  // ── Easter egg ──────────────────────────────────────────────────────────
  // The "Add to Truck" CTA doubles as a hidden yardage slider. Dragging left/
  // right across the button sweeps the quantity between the 3yd floor and one
  // full truckload (12yd, or 15yd for lightweight materials). It's undocumented
  // — the load-bar fill just happens to follow your finger. A real horizontal
  // drag suppresses the tap-to-add-to-cart click; a plain tap still adds.
  const truckDragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    pointerId: -1
  });
  const [truckSliding, setTruckSliding] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync via the useEffect below — when quantity/numLoads change externally,
  // we reflect that in qtyDraft unless the user is actively typing.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Keep the input's text in sync with the +/− stepper changes, but
  // never clobber what the user is actively typing.
  useEffect(() => {
    if (document.activeElement === qtyInputRef.current) return;
    const light = isLightweight(material);
    const fullLoad = light ? 15 : 12;
    const yards = quantity === 'multi' ? (parseInt(numLoads, 10) || 0) * fullLoad : parseInt(quantity, 10) || fullLoad;
    setQtyDraft(String(yards));
  }, [quantity, numLoads, material]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    materialSlug?: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
    bulkDiscount?: number;
    // When a custom yardage is split across loads, the last load may fall
    // below the 5yd per-load minimum and gets billed up to 5yd. This field
    // captures that adjustment so we can surface a transparent warning.
    minLoadAdjustment?: {
      requested: number;
      billable: number;
      lastLoadActual: number;
    } | null;
  } | null>(null);
  // Toggle for the "why am I billed for more yards?" info popover.
  const [showMinLoadInfo, setShowMinLoadInfo] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    const pool = heroImages;
    // Pick a fresh random image on EVERY load. To avoid showing the same
    // photo twice in a row, exclude the most recently shown index (tracked
    // in sessionStorage) when choosing.
    try {
      const key = 'hero_image_last_index';
      const lastStr = sessionStorage.getItem(key);
      const last = lastStr !== null ? parseInt(lastStr, 10) : -1;
      let index = Math.floor(Math.random() * pool.length);
      if (pool.length > 1 && index === last) {
        index = (index + 1) % pool.length;
      }
      sessionStorage.setItem(key, String(index));
      return pool[index];
    } catch {
      return pool[Math.floor(Math.random() * pool.length)];
    }
  });
  // Default to the engaged estimator stage on load — the dark/light backdrop
  // wash with the address field expanded is the primary entry point. Hitting
  // the X (handleCloseEstimator) flips this back to false, which fades the
  // backdrop out and restores the photo hero background.
  const [showEstimator, setShowEstimator] = useState(true);
  // Click-to-expand small-load rate explainer in the live price window.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Brief amber flash on the qty readout when the user taps "−" at the
  // 3 yd minimum. Replaces the static "(min)" subtext — the digit itself
  // briefly turns amber to signal the floor without adding another line
  // of clutter under the number.
  const [flashMin, setFlashMin] = useState(false);
  const flashMinTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerMinFlash = () => {
    if (flashMinTimeoutRef.current) clearTimeout(flashMinTimeoutRef.current);
    setFlashMin(true);
    flashMinTimeoutRef.current = setTimeout(() => setFlashMin(false), 700);
  };
  useEffect(() => () => {
    if (flashMinTimeoutRef.current) clearTimeout(flashMinTimeoutRef.current);
  }, []);
  // Tactile click feedback for the +/- stepper pads: on tap, "+" briefly
  // flashes green and "−" flashes amber, then both ease back to the default
  // black/white after 0.5s.
  const [plusFlash, setPlusFlash] = useState(false);
  const [minusFlash, setMinusFlash] = useState(false);
  const plusFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minusFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerPlusFlash = () => {
    if (plusFlashTimeoutRef.current) clearTimeout(plusFlashTimeoutRef.current);
    setPlusFlash(true);
    plusFlashTimeoutRef.current = setTimeout(() => setPlusFlash(false), 250);
  };
  const triggerMinusFlash = () => {
    if (minusFlashTimeoutRef.current) clearTimeout(minusFlashTimeoutRef.current);
    setMinusFlash(true);
    minusFlashTimeoutRef.current = setTimeout(() => setMinusFlash(false), 250);
  };
  useEffect(() => () => {
    if (plusFlashTimeoutRef.current) clearTimeout(plusFlashTimeoutRef.current);
    if (minusFlashTimeoutRef.current) clearTimeout(minusFlashTimeoutRef.current);
  }, []);
  // Address-first flow: address is the primary, always-visible input.
  // Once it geocodes successfully, material + quantity row reveals.
  const [addressZone, setAddressZone] = useState<{
    zone: number | null;
    fee: number;
    label: string;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [showMaterialRow, setShowMaterialRow] = useState(false);
  // True when the customer EXPLICITLY confirmed the address by picking a
  // "recent address" (which carries no coordinates). The debounced geocode
  // effect reads this and, once it resolves a valid delivery zone, opens the
  // material picker — the same outcome as picking a live suggestion. Without
  // this, a recent-address pick would confirm the zone pill but never open
  // materials, because the geocode effect otherwise stays silent to avoid
  // opening materials mid-typing.
  const pendingRecentConfirmRef = useRef(false);
  // True when the user explicitly chose "Continue anyway" after a failed
  // geocode. Quote is generated without a delivery fee; dispatch confirms
  // the address (and final fee) by phone before the truck rolls.
  const [addressUnverified, setAddressUnverified] = useState(false);
  // Custom yardage produced by the inline calculator (allows non-standard
  // single-trip quantities like 13yd, which auto-split across deliveries
  // in pricing math but display as a single line item in the quantity menu).
  const [customQty, setCustomQty] = useState<number | null>(null);
  // Manual yards input for the "More than 1 load" branch — lets users type
  // an exact yardage without going through the calculator.
  const [manualYards, setManualYards] = useState('');
  // Inline "Not sure?" calculator state for the How Much step
  const [showHeroCalc, setShowHeroCalc] = useState(false);
  const [heroCalcShape, setHeroCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [heroCalcLength, setHeroCalcLength] = useState('');
  const [heroCalcWidth, setHeroCalcWidth] = useState('');
  const [heroCalcDiameter, setHeroCalcDiameter] = useState('');
  const [heroCalcBase, setHeroCalcBase] = useState('');
  const [heroCalcHeight, setHeroCalcHeight] = useState('');
  const [heroCalcDepth, setHeroCalcDepth] = useState('3');
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  // Bumped each time a material is freshly selected, so the Add-to-Truck
  // bright-green fill re-mounts and plays its one-shot 10yd → full-load reveal
  // (decelerating at the very end) instead of snapping straight to the
  // full-load width.
  const [loadBarRevealKey, setLoadBarRevealKey] = useState(0);
  const t = useT();
  const {
    locale
  } = useLocale();
  const liveDeliveries = useLiveDeliveries();
  // Personalized greeting state — drives whether we replace the marketing
  // hero ("Spring Into Action / Fresh Starts Begin Here") with a
  // "Good morning, {firstName}" hero for returning visitors with a
  // delivered or in-flight order. Also tells us whether to suppress the
  // small WelcomeBackCard pill (only shown on the first return visit;
  // hidden after the personal greeting takes over).
  const personalGreeting = usePersonalGreeting();
  const showPersonalGreeting = personalGreeting.variant === 'in-flight' || personalGreeting.variant === 'repeat';
  const [searchParams, setSearchParams] = useSearchParams();
  // Build a "return to estimator" query string that preserves the user's
  // current estimator state (address, material, yardage). Used on every
  // outbound Link to /delivery-zones so the map page can offer a one-tap
  // "Back to Estimate" button — and Hero's mount-effect (below) will
  // re-hydrate the form from these same params on return.
  const estimatorReturnParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('from', 'estimator');
    if (address) params.set('address', address);
    if (material) params.set('material', material);
    const light = material && productMap[material] ? isLightweight(material) : false;
    const fullLoad = light ? 15 : 12;
    let yards = 0;
    if (quantity === 'multi') {
      const m = parseInt(manualYards, 10);
      if (!isNaN(m) && m > 0) {
        yards = m;
      } else if (numLoads === '5+') {
        // 5+ loads triggers the custom quote flow. For preserved-state
        // estimator return links (e.g. coming back from /delivery-zones)
        // we still need a yardage to round-trip, so estimate at the
        // bottom of the 5+ range.
        yards = 5 * fullLoad;
      } else if (numLoads) {
        const n = parseInt(numLoads, 10);
        if (!isNaN(n)) yards = n * fullLoad;
      }
    } else if (customQty && customQty > 0) {
      yards = customQty;
    } else if (quantity) {
      const n = parseInt(quantity, 10);
      if (!isNaN(n) && n > 0) yards = n;
    }
    if (yards > 0) params.set('qty', String(yards));
    return params.toString();
  }, [address, material, quantity, numLoads, manualYards, customQty]);
  // Return flow from Help Me Choose calculator: when the user comes back
  // with ?qty=N (and optionally &material=NAME&address=...), pre-fill the
  // estimator and scroll it into view so they can hit Get Estimate.
  // - qty ≤ full load → snap quantity dropdown to that single-load value
  // - qty > full load → switch to "Multiple loads" with the right load count
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    const materialParam = searchParams.get('material');
    const addressParam = searchParams.get('address');
    if (!qtyParam && !materialParam && !addressParam) return;
    if (addressParam) {
      setAddress(addressParam);
      // Returning with a confirmed address (e.g. "Back to Estimate" from the
      // delivery-zones map): make sure the estimator is engaged, and flag the
      // pending-confirm ref so the debounced geocode below re-reveals the
      // material window once it re-resolves the zone. Without this the address
      // round-trips back filled in but the material picker stays hidden with
      // no way to re-open it.
      setShowEstimator(true);
      pendingRecentConfirmRef.current = true;
    }
    if (materialParam && productMap[materialParam]) {
      setMaterial(materialParam);
    }
    if (qtyParam) {
      const n = parseInt(qtyParam, 10);
      if (!isNaN(n) && n >= 1) {
        // Determine full-load size from material if we have it, else assume
        // heavy (12 yd) — conservative default that splits more aggressively.
        const light = materialParam && productMap[materialParam] ? isLightweight(materialParam) : false;
        const fullLoad = light ? 15 : 12;
        if (n <= fullLoad) {
          // Snap to nearest valid single-load dropdown value (3..11 or fullLoad).
          const snapped = Math.max(3, Math.min(fullLoad, n));
          setQuantity(String(snapped));
          setNumLoads('');
        } else {
          // Multi-load: pick the load count that covers the requested yardage.
          // Loads 2-4 map to the corresponding dropdown option; 5 or more
          // falls into the custom-quote bucket.
          const loads = Math.ceil(n / fullLoad);
          setQuantity('multi');
          setNumLoads(loads >= 5 ? '5+' : String(loads));
        }
      }
    }
    // Clear the URL params after consuming them so a refresh doesn't
    // re-trigger this effect.
    const next = new URLSearchParams(searchParams);
    next.delete('qty');
    next.delete('material');
    next.delete('address');
    setSearchParams(next, {
      replace: true
    });
    // Scroll the estimator into view so the user lands on the pre-filled form.
    setTimeout(() => {
      estimatorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Seasonal messaging — rotates between 2 messages based on season, cycling on refresh
  const regionMeta = getRegionMeta();
  const springSummerMessages = [{
    headlineLine1: `${regionMeta.name}'s Gravel Delivery.`,
    headlineLine2: 'On Dispatch Today.',
    subtitle: 'The smartest way to order topsoil, bark mulch, and garden prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Build Season Is Here.',
    headlineLine2: 'Your Project Starts Now.',
    subtitle: 'The smartest way to order materials for driveways, patios, and landscaping — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Sun's Out, Trucks Out.",
    headlineLine2: "Let's Get It Done.",
    subtitle: 'The smartest way to order gravel, topsoil, and landscaping materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Your Yard Won't Wait.",
    headlineLine2: 'Neither Will We.',
    subtitle: 'The smartest way to order bark, compost, and garden-ready materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const fallWinterMessages = [{
    headlineLine1: `${regionMeta.name}'s Gravel Delivery.`,
    headlineLine2: 'On Dispatch Today.',
    subtitle: 'The smartest way to order drain rock, bark mulch, and grading materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'One Thumb.',
    headlineLine2: 'Full Driveway.',
    subtitle: 'The smartest way to order gravel, drainage, and winter prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Beat the Freeze.',
    headlineLine2: 'Gravel Up Before Winter.',
    subtitle: 'The smartest way to order crushed rock, drain rock, and fill materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Mud Season? Handled.',
    headlineLine2: 'Solid Ground Starts Here.',
    subtitle: 'The smartest way to order base rock, gravel, and drainage materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const [seasonalContent] = useState(() => {
    try {
      const month = new Date().getMonth(); // 0-11
      const isWarmSeason = month >= 2 && month <= 7; // Mar-Aug
      const pool = isWarmSeason ? springSummerMessages : fallWinterMessages;
      const key = 'hero_seasonal_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % pool.length;
      sessionStorage.setItem(key, String(stored + 1));
      return pool[index];
    } catch {
      return fallWinterMessages[1];
    }
  });
  // Safety net: a SELECTED material must always keep its picker visible.
  // Several paths set `material` programmatically (returning from the
  // /delivery-zones map with ?material=, the Help-Me-Choose quiz, project
  // deep-links). In those cases the address geocode round-trip — which is the
  // path that normally flips `showMaterialRow` on — may not re-run or may
  // resolve out-of-zone, leaving the material box hidden with no way to
  // re-open it even though a material is already chosen. Keying the row open
  // off the presence of a valid material guarantees it can never disappear
  // once something is selected.
  useEffect(() => {
    if (material && productMap[material]) {
      setShowMaterialRow(true);
    }
  }, [material]);
  // Once the ADDRESS is verified and the material picker reveals, the page
  // slides so the BOTTOM edge of the estimator lines up with the bottom edge
  // of the mobile viewport — bringing the full flow (address → material →
  // quantity → Add to Truck) into view and surfacing the freshly-revealed
  // "What material?" step right above the phone's bottom edge.
  //
  // The slide itself fires from the Material row's `onAnimationComplete`
  // (below), NOT here — that callback runs only AFTER the row's
  // height: 0 → auto reveal has fully laid out, so the estimator card has
  // reached its final height and its measured bottom edge is correct.
  // A fixed timeout used to fire mid-animation against a still-collapsed
  // card, which is why the slide barely moved.
  // Debounced address geocoding — runs as user types. Once we get a valid zone,
  // we reveal the material + quantity row.
  useEffect(() => {
    if (!address.trim()) {
      setAddressZone(null);
      setAddressError('');
      // Don't collapse the material row if a material is already selected —
      // a chosen material keeps its picker open regardless of address state.
      if (!(material && productMap[material])) {
        setShowMaterialRow(false);
      }
      setAddressUnverified(false);
      pendingRecentConfirmRef.current = false;
      return;
    }
    // Don't geocode (and therefore don't flash the zone pill / out-of-zone
    // "custom rate" warning) until the typed address actually LOOKS like a
    // full street address. Mid-typing fragments like "123 M" used to resolve
    // to some far-off point and pop the out-of-zone box too eagerly. Require
    // a house number AND a few street-name letters AND enough total length
    // before we bother the geocoder. A pending recent-address confirmation
    // bypasses this gate (it's already a full, previously-used address).
    const q = address.trim();
    const looksComplete = q.length >= 8 && /\d/.test(q) && (q.match(/[a-zA-Z]/g)?.length ?? 0) >= 3;
    if (!pendingRecentConfirmRef.current && !looksComplete) {
      setAddressZone(null);
      setAddressError('');
      return;
    }
    if (addressUnverified) setAddressUnverified(false);
    const timer = setTimeout(async () => {
      setIsGeocoding(true);
      setAddressError('');
      try {
        // Only append ", WA" if the address doesn't already include the state —
        // doubling the state reference can cause Nominatim to return no results
        // for otherwise valid addresses (e.g. "...Seattle, WA 98107").
        const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
        const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Try a more specific address.');
          setAddressZone(null);
          // Keep a chosen material visible even if this address can't be
          // resolved — the user can correct the address without losing it.
          if (!(material && productMap[material])) setShowMaterialRow(false);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const info = getZoneInfo(lat, lon);
          setAddressZone(info);
          // Remember the verified, in-zone address so it reappears in the
          // "Recent addresses" dropdown next time. Only persist a real
          // delivery-zone match — out-of-zone geocodes aren't confirmed
          // deliverable yet.
          if (info.zone !== null) {
            pushRecentAddress(address.trim());
          }
          // Open the material picker ONLY when the customer explicitly
          // confirmed a "recent address" (which has no coordinates and so
          // must round-trip through this geocode). A recent-address pick is
          // an explicit confirmation — equivalent to picking a live
          // suggestion — so once it resolves to a valid delivery zone we
          // reveal materials. Plain mid-typing geocodes still leave the
          // picker closed; those open via onSelect / "Continue anyway".
          if (pendingRecentConfirmRef.current) {
            pendingRecentConfirmRef.current = false;
            // Reveal materials on a valid zone. If already selected, the
            // material effect keeps the row open regardless of zone result.
            if (info.zone !== null || material && productMap[material]) {
              setShowMaterialRow(true);
            } else {
              setShowMaterialRow(false);
            }
          }
        }
      } catch {
        setAddressError('Could not verify address. Please try again.');
        // Don't collapse a chosen material on a transient geocode error.
        if (!(material && productMap[material])) setShowMaterialRow(false);
      } finally {
        setIsGeocoding(false);
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [address]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against pressing Enter in the address field before a material
    // has been picked. The visible "Get Instant Estimate" button is gated
    // on address + material + quantity, but native form submit (Enter key)
    // would bypass that gate and produce a ghost quote using the fallback
    // $55/yd default — confusing the customer with a price for a material
    // they never selected.
    if (!address) return;
    if (!material || !productMap[material]) return;
    if (!quantity) return;
    if (quantity === 'multi' && !numLoads) return;
    setIsSearching(true);
    setQuoteResult(null);
    if (addressUnverified) {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty < threshold) {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      // Manually-entered 1–2 yd loads are accepted as-is and priced with the
      // micro-load formula (1.0× material + flat $250 delivery). No bump to a
      // 3 yd minimum — we only reject tiny loads in the normal stepper flow.
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      const bulkPctU = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPctU));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPctU));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPctU));
      setQuoteResult({
        priceLow: materialsTotal,
        priceHigh: materialsTotal,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: "Delivery fee TBD — we'll confirm before dispatch",
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPctU,
        minLoadAdjustment: minAdjust
      });
      setIsSearching(false);
      setTimeout(() => {
        if (quoteResultRef.current) {
          const navHeight = document.querySelector('nav')?.offsetHeight || 80;
          const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
          });
        }
      }, 250);
      return;
    }
    try {
      // Geocode the address — only append ", WA" if the state isn't already
      // in the user's input, to avoid Nominatim zero-result quirks on doubled
      // state references like "...Seattle, WA 98107".
      const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
      const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        zoneInfo = getZoneInfo(lat, lon);
      }
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          const threshold = 10;
          isSmallLoad = qty < threshold;
          if (qty < 3) {
            surchargeMultiplier = 1;
          } else if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      let billableQty = qty;
      let minAdjust = null;
      const bulkPct = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      let totalDeliveryFee = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = 10;
        let lastLoadSurcharge = 1;
        if (lastLoad >= 3 && lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
        totalDeliveryFee = (deliveries - 1) * zoneInfo.fee + (lastLoad < 3 ? 250 : zoneInfo.fee);
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
        materialsTotal = effective * billableQty;
        totalDeliveryFee = qty < 3 ? 250 : zoneInfo.fee;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
      const total = materialsTotal + totalDeliveryFee;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: zoneInfo.zone,
        deliveryFee: totalDeliveryFee,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct,
        minLoadAdjustment: minAdjust
      });
    } catch {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '5+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else {
          const threshold = light ? 12 : 10;
          isSmallLoad = qty < threshold;
          if (qty >= threshold) {
            surchargeMultiplier = 1;
          } else {
            surchargeMultiplier = 1 + (threshold - qty) * 0.1;
          }
        }
      }
      // Manually-entered 1–2 yd loads are accepted as-is and priced with the
      // micro-load formula (1.0× material + flat $250 delivery). No bump to a
      // 3 yd minimum — we only reject tiny loads in the normal stepper flow.
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      const bulkPct2 = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      let materialsTotal = 0;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        const threshold = light ? 12 : 10;
        let lastLoadSurcharge = 1;
        if (lastLoad < threshold) {
          lastLoadSurcharge = 1 + (threshold - lastLoad) * 0.1;
        }
        const fullLoadPrice = Math.ceil(basePrice * (1 - bulkPct2));
        const partialPrice = Math.ceil(basePrice * lastLoadSurcharge * (1 - bulkPct2));
        materialsTotal = (deliveries - 1) * fullLoadYards * fullLoadPrice + lastLoad * partialPrice;
      } else {
        const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
        materialsTotal = effective * billableQty;
      }
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
      const total = materialsTotal + 100 * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct2,
        minLoadAdjustment: minAdjust
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowMaterialRow(false);
    setMaterial('');
    setQuantity('12');
    setNumLoads('');
    setQuoteResult(null);
    setAddress('');
    setAddressZone(null);
    setAddressError('');
  };
  // When an order is placed (from the estimator's "Add to truck" flow or the
  // cart), reset the estimator back to its default collapsed state. The cart
  // drawer fires an 'estimatorReset' window event on a successful order.
  useEffect(() => {
    const onOrderPlaced = () => handleCloseEstimator();
    window.addEventListener('estimatorReset', onOrderPlaced);
    return () => window.removeEventListener('estimatorReset', onOrderPlaced);
  }, []);
  // One-tap: add the quoted material to the cart, persist delivery info, open cart drawer.
  // Lets a finished estimator flow straight into checkout without re-entering address.
  // For multi-load orders, split into separate cart line items so the user sees
  // a clear breakdown (and the small-load surcharge applies correctly to the
  // partial last load).
  const handleAddToTruckFromQuote = () => {
    if (!quoteResult) return;
    const productInfo = productMap[quoteResult.material];
    if (!productInfo) return;
    // For multi-delivery orders the billed quantity equals what the
    // customer asked for (partial last loads no longer get bumped). For a
    // standalone <5 yd single delivery we still want to add 5 yd to the
    // cart (billing minimum) and flag requestedQuantity so the cart can
    // show the transparency note.
    const finalQuantity = parseInt(quoteResult.quantity) || quoteResult.minLoadAdjustment?.billable || 0;
    if (finalQuantity <= 0) return;
    const deliveries = quoteResult.deliveries || 1;
    const light = isLightweight(quoteResult.material);
    const fullLoad = light ? 15 : 12;
    if (deliveries > 1) {
      // Split across N deliveries: full loads for all but last, actual
      // remainder for the last. Each load is its own cart line item so the
      // user sees the breakdown and the small-load surcharge applies
      // naturally to a small partial last load (no 5 yd minimum bump on
      // partial loads — customer is already paying for at least one full
      // load).
      const firstLoadsTotal = (deliveries - 1) * fullLoad;
      const lastLoad = finalQuantity - firstLoadsTotal;
      for (let i = 0; i < deliveries; i++) {
        const isLast = i === deliveries - 1;
        const qty = isLast ? lastLoad : fullLoad;
        addToCart({
          category: productInfo.categoryName,
          product: `${quoteResult.material} (Delivery ${i + 1})`,
          quantity: qty,
          pricePerYard: productInfo.pricePerYard,
          source: 'estimator'
        });
      }
    } else {
      // Standalone single-delivery: flag requestedQuantity when we bumped
      // a <5 yd order up to the 5 yd order minimum so the cart can render
      // the transparency note.
      const adj = quoteResult.minLoadAdjustment;
      const requestedQuantity = adj && adj.requested > 0 && adj.requested < finalQuantity ? adj.requested : undefined;
      addToCart({
        category: productInfo.categoryName,
        product: quoteResult.material,
        quantity: finalQuantity,
        pricePerYard: productInfo.pricePerYard,
        source: 'estimator',
        requestedQuantity
      });
    }
    // Persist the address + zone + fee so CartDrawer doesn't ask again.
    setDeliveryInfo(quoteResult.address, quoteResult.deliveryFee && quoteResult.deliveries ? Math.round(quoteResult.deliveryFee / quoteResult.deliveries) : 0, quoteResult.deliveryZone);
    openCart();
  };
  return <section ref={heroSectionRef} className="relative w-full bg-[#fbfbfb] dark:bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-889" data-mp="35t8zdwo9bomg">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-890" data-mp="2w1uwdex3k6ok" />
      {/* Black overlays — same in light & dark mode */}
      <div className="absolute inset-0 z-0 md:hidden bg-black/50" data-id="element-891" data-mp="2d4jzu22unrct" />
      <div className="absolute inset-0 z-0 hidden md:block bg-gradient-to-r from-black/70 via-black/40 to-black/10" data-id="element-892" data-mp="2d4tnvk4c3ofv" />

      {/* "Bring up the lights" — when the estimator slides in, fade the photo
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      background out to a solid LIGHT backdrop (#fdfbf7) over 1 second so
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      the engaged estimator sits in a clean, light-mode environment
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      regardless of the page's light/dark theme. Fades back to the photo
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      when closed. */}
      <motion.div aria-hidden="true" className={`absolute inset-0 z-0 pointer-events-none ${isDark ? 'bg-[#131413]' : 'bg-[#f8eee3]'}`} style={{
      top: 0
    }} initial={false} animate={{
      opacity: showEstimator ? 1 : 0
    }} transition={{
      duration: 1,
      ease: 'easeInOut'
    }} data-id="element-893" data-mp="f3nu0gzdbipx" />

      {/* Soft half-round glow — a gentle dome of light pooling at the top of
                                                                                                                                                                                                                                     the hero, just under the sticky menu (the section extends 149px up
                                                                                                                                                                                                                                     behind the nav, so top:149px lands right at the visible top edge). A
                                                                                                                                                                                                                                     wide, short radial anchored at the top edge so only the lower half of
                                                                                                                                                                                                                                     the bloom shows — a soft semicircular halo that fades out in every
                                                                                                                                                                                                                                     direction. Dark mode only (needs the dark backdrop to read), sits
                                                                                                                                                                                                                                     above the backdrop wash (z-[1]) but behind the hero content (z-10),
                                                                                                                                                                                                                                     and eases in with the estimator so it glows alongside the dark stage. */}
      {isDark && <motion.div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 z-[1]" style={{
      top: '149px',
      height: '320px',
      background: 'radial-gradient(ellipse 42% 100% at 26% 0%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.035) 40%, rgba(255,255,255,0) 74%)',
      filter: 'blur(22px)'
    }} initial={false} animate={{
      opacity: showEstimator ? 1 : 0
    }} transition={{
      duration: 1,
      ease: 'easeInOut'
    }} data-id="element-894" data-mp="2djd81ttvy8qz" />}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-895" data-mp="1zmho36c3iww5">
        {/* Small WelcomeBackCard pill — only renders on the FIRST return
            visit after a delivered order (the 'just-delivered-once'
            variant). Once the user is on their 2nd+ return visit, the
            big PersonalGreeting takes over the hero slot entirely and
            this small pill steps aside to avoid duplicating the same
            affordance twice on screen. */}
        {!showPersonalGreeting && <WelcomeBackCard data-id="element-896" data-mp="1kvgw6u95mo1p" />}

        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-897" data-mp="1kyw6ljrvv8ws">
          {showPersonalGreeting ? <PersonalGreeting variant={personalGreeting.variant} firstName={personalGreeting.firstName} savedOrder={personalGreeting.savedOrder} activeOrder={personalGreeting.activeOrder} activeStepIdx={personalGreeting.activeStepIdx} estimatorOpen={showEstimator} isDark={isDark} data-id="element-898" data-mp="1g01h5yzeum3p" /> : <>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="flex items-center justify-center md:justify-start mt-6 md:mt-10 mb-4 md:mb-6" data-id="element-899" data-mp="116pw2qbl6feg">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-none text-xs sm:text-sm font-semibold tracking-widest uppercase text-white bg-[#00754a]" data-id="element-900" data-mp="ihj9gs7o93nd">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-901" data-mp="2p6to9hmazgi2" />
                  Order Gravel Like It's 2026
                </span>
              </motion.div>

              <AnimatePresence mode="wait" data-id="element-902" data-mp="2tz4pcnss58c7">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} data-id="element-903" data-mp="2g6y7mx9zatan">
                  <AnimatePresence mode="wait" data-id="element-904" data-mp="2ywd74na0nacx">
                    <motion.h1 key={locale + seasonalContent.headlineLine1} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className={`font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 leading-[1.1] ${showEstimator ? isDark ? 'text-white' : 'text-stone-900' : 'text-stone-900 dark:text-white'}`} data-id="element-905" data-mp="2yvlb5bcibam4">
                      {locale === 'es' ? t('hero.headlineLine1') : seasonalContent.headlineLine1}
                      <span className="block mt-1 md:mt-2 italic font-medium tracking-normal" style={{
                    fontFamily: '"Playfair Display", serif'
                  }} data-id="element-906" data-mp="25863d5zrrn1i">
                        {locale === 'es' ? t('hero.headlineLine2') : seasonalContent.headlineLine2}
                      </span>
                    </motion.h1>
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait" data-id="element-907" data-mp="3lkuws9dsbqty">
                <motion.p key={locale + seasonalContent.subtitle} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3,
              delay: 0.1
            }} className={`text-lg md:text-xl font-semibold font-sub mb-5 md:mb-6 max-w-2xl mx-auto md:mx-0 ${showEstimator ? isDark ? 'text-white/90' : 'text-stone-700' : 'text-stone-700 dark:text-white/90'}`} data-id="element-908" data-mp="3llwir8wc9xqg">
                  {locale === 'es' ? t('hero.subtitle') : seasonalContent.subtitle}
                </motion.p>
              </AnimatePresence>
            </>}

          {/* Review Badge + Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.25
        }} className="mb-3 md:mb-5 space-y-2" data-id="element-909" data-mp="kou958x9dai3">
            <div className="flex items-center gap-2 justify-center md:justify-start" data-id="element-910" data-mp="zllbnr28b2bx">
              {/* No pill — stars + delivery count sit directly on the hero,
               styled in a soft light tone like the reference. */}
              <div className="flex items-center gap-2.5" data-id="element-911" data-mp="26dlsb5cnn53g">
                <div className="flex" data-id="element-912" data-mp="1scsxejify7zt">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${showEstimator ? isDark ? 'text-white/85 fill-white/85' : 'text-stone-700 fill-stone-700' : 'text-stone-700 fill-stone-700 dark:text-white/85 dark:fill-white/85'}`} data-id="element-913" data-mp="1sem5824siy9e" data-mpi={i} />)}
                </div>
                <span className={`text-sm font-medium tabular-nums ${showEstimator ? isDark ? 'text-white/85' : 'text-stone-900' : 'text-stone-900 dark:text-white/85'}`} data-id="element-914" data-mp="2p2sfp5oe8kvl">
                  {liveDeliveries.toLocaleString()}+ {t('hero.deliveriesWord')}
                </span>
              </div>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col items-center md:items-start gap-3" data-id="element-915" data-mp="37tjl5w59fr4p">
              <button onClick={() => {
            // Activate the estimator, then slide the page so the live
            // order ticker bar sits 40px above the bottom of the
            // viewport.
            setShowEstimator(true);
            setTimeout(slideToTicker, 200);
          }} className="estimate-pulse-dark group block whitespace-nowrap text-center md:text-left py-3 md:py-4 my-5 font-heading text-[clamp(1.75rem,8vw,5rem)] leading-[1.05] font-extrabold tracking-tight" data-id="element-916" data-mp="164x7il8knbhy">
                <span className="underline decoration-current decoration-[6px] sm:decoration-[8px] underline-offset-[10px] sm:underline-offset-[14px] text-[#009C55] group-hover:text-[#21e169] transition-colors duration-100" data-id="element-917" data-mp="if304j70uf53">
                  {t('hero.getEstimate')}
                </span>
              </button>
              {/* These CTAs sit OVER the dark hero photo, so use the over-photo
                treatment (solid green primary + solid charcoal secondary),
                NOT isDarkText — which paints dark text on a faint dark fill
                and was invisible against the photo. */}
              <ShopByProjectCTA data-id="element-918" data-mp="2fuyxm3xjfrd9" />
            </motion.div>}
        </div>

        {/* Reassurance slideshow — "Pay on delivery, no card needed" ↔
            "30 Min from Your Driveway". Shown on the default home screen
            AND while the estimator is active (where it doubles as the
            estimator lead-in). */}
        <AnimatePresence data-id="element-919" data-mp="1s3tftj81ep0b">
          {true && <motion.div key="estimator-leadin" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className="mb-4 max-w-3xl mx-auto md:mx-0" data-id="element-920" data-mp="1s2rv0n17yo5e">
              {/* Both messages render simultaneously in the same CSS grid
               cell so the container always reserves the width of the
               widest one — eliminating the layout shift that was nudging
               the estimator on every swap. We only animate opacity. */}
              <div className="grid justify-center md:justify-start" data-id="element-921" data-mp="33q0v6ioqjcdd">
                <motion.div animate={{
              opacity: rotatingMsgIndex === 0 ? 1 : 0
            }} transition={{
              duration: 0.45,
              ease: 'easeInOut'
            }} className="row-start-1 col-start-1 flex items-center gap-2" aria-hidden={rotatingMsgIndex !== 0} data-id="element-922" data-mp="33rv5l32drm37">
                  <CheckCircle2 className="w-4 h-4 text-[#059F40] shrink-0" data-id="element-923" data-mp="2pw9n7eqnk0jr" />
                  <span className={`text-sm font-medium ${showEstimator && !isDark ? 'text-stone-800' : 'text-white'}`} data-id="element-924" data-mp="2l0u9qnwk46nv">
                    Pay on delivery, no card needed
                  </span>
                </motion.div>
                <motion.div animate={{
              opacity: rotatingMsgIndex === 1 ? 1 : 0
            }} transition={{
              duration: 0.45,
              ease: 'easeInOut'
            }} className="row-start-1 col-start-1 flex items-center gap-2" aria-hidden={rotatingMsgIndex !== 1} data-id="element-925" data-mp="2b3ftopejpjw3">
                  <CheckCircle2 className="w-4 h-4 text-[#059F40] shrink-0" data-id="element-926" data-mp="562mlmni755b" />
                  <span className={`text-sm font-medium ${showEstimator && !isDark ? 'text-stone-800' : 'text-white'}`} data-id="element-927" data-mp="a5nljx2obdi1">
                    30 Min from Your Driveway
                  </span>
                </motion.div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Estimator Card */}
        <AnimatePresence data-id="element-928" data-mp="3igrvj2qzh600">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className={`relative z-20 mb-10 max-w-[800px] mx-auto md:mx-0 scroll-mt-[180px] pt-2`} data-id="element-929" data-mp="3ncyw0g0m1pak">
              {/* Close (X) — bare icon, no pill, collapses the estimator. */}
              <button type="button" onClick={handleCloseEstimator} className={`absolute top-0 right-0 inline-flex items-center justify-center p-1 transition-colors z-30 ${isDark ? 'text-white/50 hover:text-white' : 'text-stone-500 hover:text-stone-900'}`} aria-label="Close estimator" title="Close" data-id="element-930" data-mp="3fwc4bf8plmhg">
                <X className="w-4 h-4" data-id="element-931" data-mp="3kvf1kdig6oev" />
              </button>

              {/* space-y-[27px] = 2rem (space-y-8) reduced ~15% — tightens the
               vertical gap between the Address, Material, and Quantity steps. */}
              <form onSubmit={handleSearch} className="space-y-[27px]" data-id="element-932" data-mp="24eu60v9wdf2b">
                {/* Step 1: Address (always first) */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-2.5" data-id="element-933" data-mp="1zh12k9zzaz5q">
                  <label className={cn(`block text-3xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-[#cccccc]' : 'text-[#04A05C]'}`, 'font-[300]', 'font-[800] font-[Figtree,_sans-serif]', "font-['Open_Sans',_sans-serif]", 'font-[Inter,_sans-serif]', 'text-[#00754A]', 'text-[#04A05C]', "text-[#199A6E]", "font-[Avenir,_sans-serif]", "font-[Inter,_sans-serif]")} data-id="element-934" data-mp="8cz5nkv836pf">
                    Where are we delivering?
                  </label>
                  <AddressAutocomplete value={address} onChange={next => {
                setAddress(next);
                // Typing/editing the address means it is no longer a
                // confirmed delivery location — re-gate the material
                // picker so it stays closed until the customer
                // explicitly confirms the new address (picks a
                // suggestion or clicks "Continue anyway"). Without this,
                // a previously-confirmed address that got edited would
                // leave materials open against an unverified address.
                setShowMaterialRow(false);
                // If the user edits the address after we've already
                // produced a quote, invalidate the quote — otherwise
                // clicking "Add to Cart" would push the OLD address
                // (the one tied to the prior quote) into the cart
                // instead of what the customer just typed. Forcing
                // a re-quote guarantees the cart and the estimator
                // always agree on the delivery address.
                if (quoteResult && next !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} onSelect={s => {
                // User picked a suggestion — skip the debounced geocode
                // and act on the coordinates directly.
                const info = getZoneInfo(s.lat, s.lon);
                setAddressZone(info);
                setAddressError('');
                // Remember this confirmed address so it reappears in the
                // "Recent addresses" dropdown on the next visit.
                pushRecentAddress(s.fullAddress);
                setRecentAddrTick(n => n + 1);
                // Only open materials when the picked address is verified
                // inside a delivery zone. An out-of-zone pick keeps
                // materials closed until the customer acknowledges the
                // "custom rate / can't verify" warning box.
                setShowMaterialRow(info.zone !== null);
                // Suggestion-pick is also an address change, so any
                // existing quote is now stale — clear it so the user
                // re-runs the estimate against the new address.
                if (quoteResult && s.fullAddress !== quoteResult.address) {
                  setQuoteResult(null);
                }
              }} onSelectRecent={() => {
                // A recent address has no coordinates, so it must
                // re-geocode. Flag it as an explicit confirmation so
                // the debounced geocode effect opens the material
                // picker once it resolves a valid delivery zone.
                pendingRecentConfirmRef.current = true;
              }} placeholder={`e.g. 123 Main St, ${getRegionMeta().cities[0]}`} required autoComplete="off-mp-estimator-address" rightIcon={!isGeocoding && addressZone?.zone ? <CheckCircle2 className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#00704A]'}`} data-id="element-936" data-mp="3tf8x3bv5159h" /> : undefined} isLoading={isGeocoding} variant="light" recentAddresses={recentAddresses} containerClassName={isDark ? 'estimator-addr-wrap--dark' : 'estimator-addr-wrap--light'} inputClassName={`estimator-green-input ${isDark ? 'estimator-green-input--dark' : 'estimator-green-input--light'} block w-full pl-6 pr-14 h-[68px] sm:h-[80px] rounded-[20px] border-0 text-xl sm:text-2xl font-medium focus:outline-none transition-all truncate`} data-id="element-935" data-mp="r8gwplqlf7ur" />
                  <AnimatePresence mode="wait" data-id="element-937" data-mp="1o4hs2eq0aqcb">
                    {addressError && !isGeocoding && !addressUnverified && <motion.div key="err" initial={{
                  opacity: 0,
                  y: -4,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-938" data-mp="1t31eeg8ii457">
                        <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-939" data-mp="viycxm2a96s2">
                          <div className="flex items-start gap-2.5" data-id="element-940" data-mp="10h9n5xwhpjat">
                            <Info className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-941" data-mp="mgp36yeu9hfq" />
                            <div className="flex-1 min-w-0" data-id="element-942" data-mp="reqnfmo03mss">
                              <p className={`text-sm font-bold leading-snug ${isDark ? 'text-amber-200' : 'text-amber-800'}`} data-id="element-943" data-mp="7u042xoi169v">
                                We couldn't auto-verify this address
                              </p>
                              <p className={`mt-1 text-xs leading-relaxed ${isDark ? 'text-white/80' : 'text-stone-700'}`} data-id="element-944" data-mp="gvzrc7q3x6l9">
                                No problem — you can still get a quote for the
                                materials. Our dispatcher will reach out to
                                confirm the delivery address and final fee
                                before your truck is scheduled.
                              </p>
                              <div className="mt-3 flex flex-col sm:flex-row gap-2" data-id="element-945" data-mp="vjui3ikk10vp">
                                <a href="tel:206-619-3742" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-xs font-bold transition-colors" data-id="element-946" data-mp="vlxeqlg7ps9e">
                                  <Phone className="w-3.5 h-3.5 text-[#059F40]" data-id="element-947" data-mp="27cxjl9uw68x" />
                                  Call dispatch · (206) 619-3742
                                </a>
                                <button type="button" onClick={() => {
                            setAddressUnverified(true);
                            setAddressError('');
                            setAddressZone(null);
                            setShowMaterialRow(true);
                          }} className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-[#059F40] hover:bg-[#048036] text-white text-xs font-bold transition-colors" data-id="element-948" data-mp="73tkic5bkekf">
                                  Continue anyway
                                  <ArrowRight className="w-3.5 h-3.5" data-id="element-949" data-mp="yx3q3eyl7jc3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                    {addressUnverified && !isGeocoding && <motion.div key="unverified" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-950" data-mp="2t5r5vy568t0a">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border font-bold ${isDark ? 'border-amber-400/40 text-amber-300' : 'border-amber-500/50 text-amber-800'}`} data-id="element-951" data-mp="2ffdwdrcmt68u">
                          <Info className="w-3 h-3" data-id="element-952" data-mp="372w3rauqelkg" />
                          Address pending confirmation
                        </span>
                        <span className="text-stone-700 dark:text-white/70" data-id="element-953" data-mp="2y4tyuk7vn8rw">
                          Dispatch will confirm by phone before delivery
                        </span>
                      </motion.div>}
                    {addressZone && addressZone.zone !== null && !isGeocoding && <motion.div key="zone" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-954" data-mp="3altmq2rhc9js">
                          {(() => {
                    // Pull the per-zone color so the pill mirrors
                    // the color the customer would see for their
                    // zone on the delivery map. Inline styles
                    // here because Tailwind can't safely build
                    // arbitrary runtime classes. Fee is folded
                    // INTO the pill so the whole thing reads as
                    // one tappable affordance.
                    const zoneColor = ZONE_COLORS[addressZone.zone as number] || '#059F40';
                    const feeLabel = addressZone.fee === 0 ? t('hero.estimator.freeDelivery') : `+${addressZone.fee} ${t('hero.estimator.plusDelivery')}`;
                    return <Link to={`/delivery-zones?${estimatorReturnParams}`} className="group inline-flex items-center gap-1.5 rounded-md py-[6px] bg-transparent font-bold uppercase tracking-wide transition-colors hover:opacity-80 text-[13px]" style={{
                      color: zoneColor
                    }} title="View on delivery zones map" data-id="element-955" data-mp="25d8x5h6vkkho">
                                <MapPin className="w-3.5 h-3.5" data-id="element-956" data-mp="1mnsvgqjgelzo" />
                                {t('hero.quote.zone')} {addressZone.zone}
                                <span className="opacity-60" data-id="element-957" data-mp="1hqajera53qng">·</span>
                                {feeLabel}
                              </Link>;
                  })()}
                        </motion.div>}
                    {addressZone && addressZone.zone === null && !addressUnverified && !isGeocoding && <motion.div key="custom" initial={{
                  opacity: 0,
                  y: -4,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-958" data-mp="13wt4jjsef23r">
                          {/* Out-of-zone address — geocoded fine but lands
                           outside our standard delivery zones. Treated as
                           "can't be auto-verified": materials stay closed
                           until the customer taps "Continue anyway" to
                           acknowledge the custom-rate quote path. */}
                          <div className="mt-1 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-959" data-mp="2p109stwkgd3v">
                            <div className="flex items-start gap-2.5" data-id="element-960" data-mp="2tzjtsorlnetl">
                              <Info className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-961" data-mp="4bj5410lpiio" />
                              <div className="flex-1 min-w-0" data-id="element-962" data-mp="99krovx8jzyw">
                                <p className={`text-sm font-bold leading-snug ${isDark ? 'text-amber-200' : 'text-amber-800'}`} data-id="element-963" data-mp="jeatn1sn3434">
                                  {t('hero.estimator.outsideZones')}
                                </p>
                                <p className={`mt-1 text-xs leading-relaxed ${isDark ? 'text-white/80' : 'text-stone-700'}`} data-id="element-964" data-mp="9npw7hopc5ls">
                                  We can still deliver here — our dispatcher
                                  will confirm the exact delivery rate by phone
                                  before your truck is scheduled.
                                </p>
                                <div className="mt-3 flex flex-col sm:flex-row gap-2" data-id="element-965" data-mp="3mwkx2k20e8tt">
                                  <a href="tel:206-619-3742" className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-xs font-bold transition-colors" data-id="element-966" data-mp="3ivsnfpp861x0">
                                    <Phone className="w-3.5 h-3.5 text-[#059F40]" data-id="element-967" data-mp="1h3vzfglvezlc" />
                                    Call dispatch · (206) 619-3742
                                  </a>
                                  <button type="button" onClick={() => {
                            setAddressUnverified(true);
                            setShowMaterialRow(true);
                          }} className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-[#059F40] hover:bg-[#048036] text-white text-xs font-bold transition-colors" data-id="element-968" data-mp="24cy2k43dwhjx">
                                    Continue anyway
                                    <ArrowRight className="w-3.5 h-3.5" data-id="element-969" data-mp="1lklowl7a5tuh" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                  </AnimatePresence>
                </motion.div>

                {/* Step 2: Material */}
                <AnimatePresence data-id="element-970" data-mp="36y892pkdg001">
                  {showMaterialRow && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} onAnimationComplete={() => {
                // Address just confirmed and the "What material?" row
                // has finished revealing. At THIS stage (no material
                // picked yet) we slide up only until the live order
                // ticker bar comes fully into view — NOT all the way to
                // align the short estimator's bottom with the screen.
                // Once a material is picked, the quantity row's own
                // onAnimationComplete takes over and slides the now-
                // taller estimator flush to the viewport bottom.
                if (!(material && productMap[material])) {
                  slideToTicker();
                }
              }} className="space-y-2 overflow-visible" data-id="element-971" data-mp="3h5aw3hgtxulj">
                      <label className={cn(`block text-3xl sm:text-4xl font-extrabold tracking-tight mb-2.5 mt-3 ${isDark ? 'text-[#cccccc]' : 'text-[#03C883]'}`, 'font-[300]', 'font-[800]', "font-['Open_Sans',_sans-serif]", 'font-[Inter,_sans-serif]', 'text-[#00754A]', 'text-[#04A05C]', "text-[#199A6E]")} data-id="element-972" data-mp="1egztzfi773wb">
                        What material?
                      </label>
                      <CustomSelect value={material} onChange={val => {
                  setMaterial(val);
                  // Auto-bump the quantity dropdown to the per-product
                  // default when material changes. Soil/Mulch/Org.
                  // products (except 50/50 Mix) default to 15 yd —
                  // a full light-material truckload and the typical
                  // homeowner spring-project size. Everything else
                  // (and 50/50 Mix) stays on 12 yd.
                  setQuantity(getDefaultQuantity(val));
                  // Replay the Add-to-Truck load-bar reveal: the bright
                  // fill starts at the 10yd mark and eases up to the
                  // default full load, slowing at the very end.
                  setLoadBarRevealKey(k => k + 1);
                }} placeholder="Select Material" options={materialOptions} isDark={isDark} data-id="element-973" data-mp="vmud6raowe3m" />
                      <p className={`text-xs pt-1 leading-relaxed font-[Manrope,_sans-serif] ${isDark ? 'text-white/60' : 'text-stone-600'}`} data-id="element-974" data-mp="25ohx3n7wbgmz">
                        Not sure what you need?{' '}
                        <Link to={`/help?from=estimator${address ? `&address=${encodeURIComponent(address)}` : ''}`} className="font-semibold hover:underline whitespace-nowrap text-[#00905B]" data-id="element-975" data-mp="2elsapf86rhv2">
                          Take the 30-sec material quiz →
                        </Link>
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* Step 3: How much — only reveals after a material is picked */}
                <AnimatePresence data-id="element-976" data-mp="30ew8snmhvzao">
                  {showMaterialRow && material && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35,
                delay: 0.05
              }} onAnimationComplete={() => {
                // Picking a material reveals this quantity + price +
                // Add-to-Truck block, which grows the estimator card
                // taller. The Material row's onAnimationComplete only
                // fires once (on first reveal after address confirm),
                // so we re-run the slide HERE once this row has fully
                // laid out — keeping the bottom of the now-taller
                // estimator flush with the bottom of the phone.
                slideEstimatorBottomToViewport();
              }} className="space-y-2 overflow-visible" data-id="element-977" data-mp="3fbdk5euopdx8">
                      <label className={cn(`block text-3xl sm:text-4xl font-extrabold tracking-tight mb-2.5 mt-3 ${isDark ? 'text-[#A79A8C]' : 'text-[#04A05C]'}`, 'font-[300]', "font-[800] font-['Open_Sans',_sans-serif]", 'font-[Inter,_sans-serif]', 'text-[#00754A]', 'text-[#04A05C]', "text-[#199A6E]")} data-id="element-978" data-mp="4hyi0kbsoivp">
                        How much?
                      </label>
                      {/* Floating stepper — matches Product page and
                       Services hub: big bold + and − pads, no container
                       background. Below full load: +1 yd. At/above:
                       snap by full load (2, 3, 4 loads). + at 4 loads
                       flips to amber 5+ warning link; − clears it.
                       Manipulates the existing quantity/numLoads pair
                       so all downstream quote math stays unchanged. */}
                      {(() => {
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const showWarning = quantity === 'multi' && (parseInt(numLoads, 10) >= 5 || numLoads === '5+');
                  const currentYards = quantity === 'multi' ? (parseInt(numLoads, 10) || 2) * fullLoad : parseInt(quantity, 10) || fullLoad;
                  const loadCount = currentYards > fullLoad ? Math.ceil(currentYards / fullLoad) : 1;
                  const isFullLoad = currentYards === fullLoad;
                  const isMin = currentYards === 3;
                  const handleDec = () => {
                    triggerMinusFlash();
                    if (currentYards <= fullLoad) {
                      if (currentYards > 3) {
                        setQuantity(String(currentYards - 1));
                        setNumLoads('');
                        setCustomQty(null);
                      } else {
                        // At the 3 yd floor — flash the readout
                        // amber so the customer feels the limit
                        // instead of seeing the tap do nothing.
                        triggerMinFlash();
                      }
                    } else {
                      const loads = Math.round(currentYards / fullLoad);
                      if (loads <= 2) {
                        setQuantity(String(fullLoad));
                        setNumLoads('');
                      } else {
                        setQuantity('multi');
                        setNumLoads(String(loads - 1));
                      }
                    }
                    setCustomQty(null);
                  };
                  const handleInc = () => {
                    triggerPlusFlash();
                    if (currentYards < fullLoad) {
                      setQuantity(String(currentYards + 1));
                      setNumLoads('');
                    } else {
                      const loads = currentYards === fullLoad ? 1 : Math.round(currentYards / fullLoad);
                      // Cap at 8 loads. Past 4 loads the primary CTA
                      // flips to the amber "text us" button — but
                      // the stepper still works so the customer can
                      // show us the exact yardage they need.
                      if (loads >= 8) {
                        setCustomQty(null);
                        return;
                      }
                      setQuantity('multi');
                      setNumLoads(String(loads + 1));
                    }
                    setCustomQty(null);
                  };
                  return <div className="space-y-2" data-id="element-979" data-mp="1vkkys9g5mlwo">
                            <div className="flex items-stretch gap-4 sm:gap-6 pb-5" data-id="element-980" data-mp="29jayzxy9t1p9">
                              {/* Yardage readout — matched to the height of the
                               address + material boxes above (py-5 sm:py-6),
                               with square corners. */}
                              <div className={`flex-1 flex items-center px-6 sm:px-8 h-[68px] sm:h-[80px] rounded-[20px] border ${isDark ? 'bg-white/[0.07] border-white/[0.125] text-white backdrop-blur-md' : 'bg-white border-[#d6d3d1] text-stone-900'} text-3xl sm:text-4xl font-extrabold tabular-nums leading-none`} data-id="element-981" data-mp="2t303s8hjsdlp">
                                {(() => {
                          // Digit color encodes state:
                          // - amber flash when user hits the
                          //   3 yd floor (replaces the old
                          //   "(min)" subtext)
                          // - subtle sage green when exactly
                          //   at one full load (replaces the
                          //   old "(full load)" subtext)
                          // - neutral otherwise
                          const tintClass = isMin ? isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]' : isDark ? 'text-white' : 'text-stone-900';
                          // flashMin still triggers a brief
                          // tactile pulse when "−" is tapped at
                          // the floor, layered on top of the
                          // persistent amber by upping opacity.
                          void flashMin;
                          const commit = () => {
                            const parsed = parseInt(qtyDraft, 10);
                            if (isNaN(parsed) || parsed <= 0) {
                              setQtyDraft(String(currentYards));
                              return;
                            }
                            const max = fullLoad * 8;
                            const clamped = Math.min(max, Math.max(1, parsed));
                            // Honor the exact typed yardage. We
                            // used to snap to the nearest full
                            // load (e.g. type 25, get 30) which
                            // confused customers who entered a
                            // calculator-derived number. The
                            // loadCount derivation downstream
                            // (Math.ceil(yards / fullLoad)) takes
                            // care of multi-delivery sizing for
                            // the delivery fee.
                            setQuantity(String(clamped));
                            setNumLoads('');
                            setQtyDraft(String(clamped));
                            setCustomQty(null);
                          };
                          return <>
                                      <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                              const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                              setQtyDraft(v);
                            }} onBlur={commit} onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                (e.currentTarget as HTMLInputElement).blur();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setQtyDraft(String(currentYards));
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }} aria-label="Quantity in yards" style={{
                              width: `${Math.max(1, qtyDraft.length)}ch`
                            }} className={`bg-transparent border-0 outline-none text-center text-3xl sm:text-4xl font-extrabold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${tintClass}`} data-id="element-982" data-mp="323pv87whoxzy" />
                                      <span className={cn(`hidden sm:inline ml-2 transition-colors ${tintClass}`, 'font-[800]', 'font-[800]')} data-id="element-983" data-mp="336o4ctyehai9">
                                        yards
                                      </span>
                                      <span className={`sm:hidden ml-1.5 transition-colors ${tintClass}`} data-id="element-984" data-mp="2u3wpek6bfwlv">
                                        yd
                                      </span>
                                      {(loadCount > 1 || loadCount === 1 && currentYards === (isLightweight(material) ? 15 : 12)) && <span className={`ml-2 text-xs sm:text-sm font-medium align-baseline tracking-tight whitespace-nowrap ${isDark ? 'text-white/70' : 'text-[#00704A]/70'}`} data-id="element-985" data-mp="2ffs5jcvj1f1i">
                                          ({loadCount}{' '}
                                          {loadCount === 1 ? 'load' : 'loads'})
                                        </span>}
                                    </>;
                        })()}
                              </div>
                              {/* −/+ controls — each sits inside a circular
                               pad that uses the SAME surface/border as the
                               yardage readout box on the left, so the row
                               reads as a matched set. */}
                              <div className="flex items-center justify-end gap-2.5 sm:gap-3 shrink-0" data-id="element-986" data-mp="1huh4xw5rdar7">
                                <button type="button" onClick={handleDec} aria-label="Decrease quantity" style={{
                          WebkitTapHighlightColor: 'transparent'
                        }} className={`group flex items-center justify-center w-[64px] sm:w-[72px] self-stretch shrink-0 bg-transparent border-0 transition-colors duration-150 ${minusFlash ? isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]' : isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-987" data-mp="25k36urdim42w">
                                  <Minus className="w-9 h-9 sm:w-11 sm:h-11 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={3} data-id="element-988" data-mp="2ohmgnx7srqqd" />
                                </button>
                                <button type="button" onClick={handleInc} aria-label="Increase quantity" style={{
                          WebkitTapHighlightColor: 'transparent'
                        }} className={`group flex items-center justify-center w-[64px] sm:w-[72px] self-stretch shrink-0 bg-transparent border-0 transition-colors duration-150 ${plusFlash ? isDark ? 'text-[#22c95c]' : 'text-[#059F40]' : isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-989" data-mp="hsu8mydgfahu">
                                  <Plus className="w-9 h-9 sm:w-11 sm:h-11 transition-transform duration-150 ease-out group-active:scale-125" strokeWidth={3} data-id="element-990" data-mp="3q3xcwtf693fc" />
                                </button>
                              </div>
                            </div>

                            {/* Live price window — updates in real time as
                             the customer adjusts address / material /
                             stepper. Shows the full picture: materials
                             with small-load surcharge applied, per-load
                             delivery from the zone lookup, and the
                             grand total. This replaces the old
                             post-submit estimate interstitial. */}
                            {productMap[material] && (() => {
                      const basePrice = productMap[material].pricePerYard;
                      // Tiered bulk discount — mirrors
                      // CartContext math so the live total
                      // matches what they see in the cart.
                      // 5% at 1 full load (light) / 2 full
                      // loads (heavy). 10% at 4 full loads.
                      const _light = isLightweight(material);
                      const _fullLoad = _light ? 15 : 12;
                      const bulkPct = currentYards >= _fullLoad * 4 ? 0.1 : currentYards >= (_light ? _fullLoad : _fullLoad * 2) ? 0.05 : 0;
                      // Split into full-load yards (charged at
                      // base) and partial-yards (charged at
                      // small-load surcharge). When both are
                      // non-zero we render two material lines
                      // so the customer sees exactly which
                      // yards trigger the higher rate.
                      const _fullLoadCount = Math.floor(currentYards / _fullLoad);
                      const _fullLoadYards = _fullLoadCount * _fullLoad;
                      const _partialYards = currentYards - _fullLoadYards;
                      const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, material) : 1;
                      const fullLoadPerYd = Math.ceil(basePrice * (1 - bulkPct));
                      const partialPerYd = _partialYards > 0 ? Math.ceil(basePrice * _partialSurcharge * (1 - bulkPct)) : 0;
                      const fullLoadSubtotal = _fullLoadYards * fullLoadPerYd;
                      const partialSubtotal = _partialYards * partialPerYd;
                      const materialsSubtotal = fullLoadSubtotal + partialSubtotal;
                      const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                      // For the single-line fallback (no
                      // mixed breakdown), use the per-yard
                      // rate that actually applies.
                      const singleLineSurcharge = getSmallLoadSurcharge(currentYards, material);
                      const singleLinePerYd = Math.ceil(basePrice * singleLineSurcharge * (1 - bulkPct));
                      const isSmallLoad = _partialSurcharge > 1;
                      // Micro-load: a manually-entered 1–2 yd single
                      // load. Priced at the base per-yard rate (no
                      // surcharge) plus a flat $250 delivery fee that
                      // covers the truck run. Used to relabel the
                      // materials line and surface the flat-fee note.
                      const isMicroLoad = loadCount === 1 && currentYards > 0 && currentYards < 3;
                      const zoneFee = addressZone && addressZone.zone !== null ? addressZone.fee : null;
                      // Delivery total mirrors the Add-to-Truck
                      // button: each full load pays the zone fee, but
                      // a last/partial load under 3 yd swaps to the
                      // flat $250 micro-load fee.
                      const _lastLoadYards = currentYards - (loadCount - 1) * _fullLoad;
                      // Is the last (partial) load a micro-load
                      // (1–2 yd)? Those carry a flat $250 delivery
                      // fee instead of the zone rate, so they're
                      // billed — and displayed — separately from the
                      // standard zone-fee loads.
                      const _microLoadPresent = _lastLoadYards > 0 && _lastLoadYards < 3;
                      // Loads billed at the standard zone fee (every
                      // load except a trailing micro-load).
                      const _standardLoadCount = _microLoadPresent ? loadCount - 1 : loadCount;
                      const _standardDeliverySubtotal = zoneFee !== null ? _standardLoadCount * zoneFee : null;
                      const _microDeliveryFee = _microLoadPresent ? 250 : 0;
                      const deliveryTotal = zoneFee !== null ? (_standardDeliverySubtotal || 0) + _microDeliveryFee : null;
                      const grandTotal = deliveryTotal !== null ? materialsSubtotal + deliveryTotal : null;
                      // Past 4 full loads (≥ 5 loads) the
                      // standard discount tier no longer
                      // applies — these orders qualify for
                      // larger custom discounts negotiated
                      // by phone/text. Blur the breakdown
                      // so the customer doesn't anchor on
                      // the displayed number, and surface a
                      // clear "text us" handoff overlay.
                      const isLargeOrder = loadCount >= 5;
                      return <div className="relative mt-3" data-id="element-991" data-mp="3m2bm2w9398ft">
                                    <motion.div key={`${currentYards}-${material}-${zoneFee ?? 'na'}`} initial={{
                          opacity: 0,
                          y: -4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.2
                        }} aria-hidden={isLargeOrder} className={`pt-1 space-y-2 transition-all duration-300 ${isLargeOrder ? 'blur-[6px] opacity-50 pointer-events-none select-none' : ''}`} data-id="element-992" data-mp="3h7o6jmw2bodd">
                                      {/* Materials section — splits
                                       into TWO lines when the order is a
                                       full load + partial mix, so the
                                       customer sees exactly which yards
                                       are at the full-load rate and which
                                       yards trigger the small-load
                                       surcharge. */}
                                      {hasMixedBreakdown ? <div data-id="element-993" data-mp="1mkav33le70e1">
                                          <div className={`text-xs uppercase tracking-wider font-bold mb-1 ${isDark ? 'text-white' : 'text-stone-500'}`} data-id="element-994" data-mp="1mlcga0w4h39h">
                                            Materials
                                          </div>
                                          {/* Full-load line */}
                                          <div className="flex items-baseline justify-between gap-3" data-id="element-995" data-mp="13u2wmysn0y46">
                                            <div className={`min-w-0 text-xs flex items-center flex-wrap gap-x-2 ${isDark ? 'text-white' : 'text-stone-600'}`} data-id="element-996" data-mp="2jatk0abjof8j">
                                              <span data-id="element-997" data-mp="2f8orxg0qvqo0">
                                                ${fullLoadPerYd}/yd ×{' '}
                                                {_fullLoadYards} yd
                                              </span>
                                              {bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-998" data-mp="2cvcxuscvqmbc">
                                                  {Math.round(bulkPct * 100)}%
                                                  bulk discount
                                                </span> : null}
                                            </div>
                                            <div className={`text-sm font-bold tabular-nums shrink-0 ${isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-999" data-mp="3akem3rkdhvg2">
                                              $
                                              {fullLoadSubtotal.toLocaleString()}
                                            </div>
                                          </div>
                                          {/* Partial-load line. Only tag
                                           it "Small-load rate" when the
                                           partial actually triggers a
                                           surcharge (below threshold). A
                                           partial at/above threshold —
                                           e.g. an 11 yd remainder on a
                                           35 yd order — runs at the
                                           full-load/bulk rate. */}
                                          <div className="flex items-baseline justify-between gap-3 mt-0.5" data-id="element-1000" data-mp="3fez7ihl4cx2t">
                                            <div className={`min-w-0 text-xs flex items-center flex-wrap gap-x-2 ${isDark ? 'text-white' : 'text-stone-600'}`} data-id="element-1001" data-mp="3ah62hrvjy944">
                                              <span data-id="element-1002" data-mp="31fgb2m1o7a6c">
                                                ${partialPerYd}/yd ×{' '}
                                                {_partialYards} yd
                                              </span>
                                              {_partialSurcharge > 1 ? <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} className={`inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline focus:outline-none ${isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]'}`} aria-expanded={showSmallLoadInfo} data-id="element-1003" data-mp="1by2plgrkxs0d">
                                                  Small-load rate
                                                  <Info className="w-3 h-3" data-id="element-1004" data-mp="t94byzgo31f7" />
                                                </button> : bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-1005" data-mp="y7pcz13qtp4p">
                                                  {Math.round(bulkPct * 100)}%
                                                  bulk discount
                                                </span> : null}
                                            </div>
                                            <div className={`text-sm font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1006" data-mp="eix2384q6qq4">
                                              $
                                              {partialSubtotal.toLocaleString()}
                                            </div>
                                          </div>
                                        </div> : <div className="flex items-baseline justify-between gap-[12px]" data-id="element-1007" data-mp="55y3p3rbc8td">
                                          <div className="min-w-0 flex items-baseline flex-wrap gap-x-2" data-id="element-1008" data-mp="e4t8qcimfcv3">
                                            <div className={cn(`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`, 'text-[16px]', 'font-[Inter,_sans-serif]')} data-id="element-1009" data-mp="95qbhe8vuaxo">
                                              Materials
                                            </div>
                                            <div className={`text-xs flex items-center flex-wrap gap-x-2 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1010" data-mp="2pu2k3g2kzrix">
                                              <span className="text-[14px]" data-id="element-1011" data-mp="2kx3sb46e3wnw">
                                                ${singleLinePerYd}/yd ×{' '}
                                                {currentYards} yd
                                              </span>
                                              {isSmallLoad ? <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} className={`inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline focus:outline-none ${isDark ? 'text-[#F6A93B]' : 'text-[#CB6E0A]'}`} aria-expanded={showSmallLoadInfo} data-id="element-1012" data-mp="qmvxk3pz5cmy">
                                                  Small-load rate
                                                  <Info className="w-3 h-3" data-id="element-1013" data-mp="7pkz2lc62n8k" />
                                                </button> : bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-1014" data-mp="cop7h9r9ogxu">
                                                  {Math.round(bulkPct * 100)}%
                                                  bulk discount
                                                </span> : null}
                                            </div>
                                          </div>
                                          <div className={`text-base font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1015" data-mp="20kdj4fh0c682">
                                            $
                                            {materialsSubtotal.toLocaleString()}
                                          </div>
                                        </div>}
                                      {/* Small-load explainer — appears on
                                       tap of the amber chip above. Short,
                                       plain language; not a full essay. */}
                                      {isSmallLoad && showSmallLoadInfo && <motion.div initial={{
                            opacity: 0,
                            height: 0
                          }} animate={{
                            opacity: 1,
                            height: 'auto'
                          }} exit={{
                            opacity: 0,
                            height: 0
                          }} className="overflow-hidden" data-id="element-1016" data-mp="29ltjdue3yryk">
                                          <div className={`text-[11px] rounded-lg px-3 py-2 leading-relaxed border ${isDark ? 'text-[#F6A93B]/90 bg-[#F6A93B]/10 border-[#F6A93B]/30' : 'text-[#CB6E0A] bg-[#CB6E0A]/5 border-[#CB6E0A]/30'}`} data-id="element-1017" data-mp="3jkcqj4js9qfn">
                                            Orders under a full load share the
                                            same truck run, so the per-yard rate
                                            is slightly higher. It scales down
                                            to the full-load rate at 10+ yd.
                                          </div>
                                        </motion.div>}
                                      {/* Delivery row — one line: label +
                                       zone inline, fee on the right. Zone
                                       label uses the zone's map color so
                                       the breakdown matches the pill
                                       above. */}
                                      {zoneFee !== null && deliveryTotal !== null ? <div className="space-y-1.5" data-id="element-1018" data-mp="1fdkdkohjog6b">
                                          {/* Standard zone-fee loads — combined
                                           into ONE row when they all cost the
                                           same (e.g. "Zone 1 × 2 loads"). Only
                                           shown when at least one full/standard
                                           load exists. */}
                                          {_standardLoadCount > 0 && _standardDeliverySubtotal !== null && <div className="flex items-center justify-between gap-3" data-id="element-1019" data-mp="rn6s5gtgtfkp">
                                                <div className={`text-xs min-w-0 truncate ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1020" data-mp="3wuctkvw4gk3">
                                                  <span className={cn(`uppercase tracking-wider font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`, 'text-[16px]')} data-id="element-1021" data-mp="2amnxutqt7tcz">
                                                    Delivery
                                                  </span>
                                                  <span className={`mx-1.5 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1022" data-mp="26lch7dpwyigx">
                                                    ·
                                                  </span>
                                                  <span className={cn(`font-[400] ${isDark ? 'text-white/90' : 'text-black/90'}`, 'text-[14px]')} data-id="element-1023" data-mp="2ybgz95q7atez">
                                                    Zone {addressZone?.zone}
                                                  </span>
                                                  {_standardLoadCount > 1 && ` × ${_standardLoadCount} loads`}
                                                </div>
                                                <div className={`text-base font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1024" data-mp="1dukgc2xwl5n9">
                                                  {_standardDeliverySubtotal === 0 ? 'Free' : `${_standardDeliverySubtotal.toLocaleString()}`}
                                                </div>
                                              </div>}
                                          {/* Micro-load delivery — its own row
                                           with a short description, since it's a
                                           different (flat $250) price from the
                                           zone-fee loads above. */}
                                          {_microLoadPresent && <div className="flex items-start justify-between gap-3" data-id="element-1025" data-mp="1a0boy99hcxui">
                                              <div className={`text-xs min-w-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1026" data-mp="3qph3j9ubczwu">
                                                <span className={`uppercase tracking-wider font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1027" data-mp="3usvkc2q2tkbc">
                                                  Micro-load delivery
                                                </span>
                                                <span className={`block text-[11px] mt-0.5 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1028" data-mp="zjrdxe8cq5a">
                                                  {_lastLoadYards} yd load ·
                                                  flat fee (under 3 yd)
                                                </span>
                                              </div>
                                              <div className={`text-base font-bold tabular-nums shrink-0 ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1029" data-mp="3e7d0tkq7ru9f">
                                                $
                                                {_microDeliveryFee.toLocaleString()}
                                              </div>
                                            </div>}
                                        </div> : <div className={`text-xs italic ${isDark ? 'text-white/50' : 'text-stone-400'}`} data-id="element-1030" data-mp="36go4ih9j6qor">
                                          Enter your address to include delivery
                                        </div>}
                                      {/* Micro-load (1–2 yd) flat-fee note —
                                       explains why delivery jumps from the
                                       zone rate to a flat $250, and nudges a
                                       round-up to drop it. */}
                                      {isMicroLoad && zoneFee !== null && <div className={`flex items-start gap-1.5 text-[11px] rounded-lg px-3 py-2 leading-relaxed border ${isDark ? 'text-[#F6A93B]/90 bg-[#F6A93B]/10 border-[#F6A93B]/30' : 'text-[#CB6E0A] bg-[#CB6E0A]/5 border-[#CB6E0A]/30'}`} data-id="element-1031" data-mp="2whykahu5wu5c">
                                          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-1032" data-mp="btkbyceagu7g" />
                                          <span data-id="element-1033" data-mp="gopwb6s1m0tf">
                                            Loads under 3 yd use a flat $250
                                            micro-load delivery fee. Round up to{' '}
                                            <span className={`font-bold ${isDark ? 'text-white' : 'text-stone-900'}`} data-id="element-1034" data-mp="1wble36wpvnfl">
                                              3+ yd
                                            </span>{' '}
                                            to switch to the Zone{' '}
                                            {addressZone?.zone} rate (${zoneFee}
                                            ).
                                          </span>
                                        </div>}
                                      {/* Divider + grand total */}
                                      {grandTotal !== null && <>
                                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-stone-300'}`} data-id="element-1035" data-mp="32ww2jn00hz7i" />
                                          <div className="flex items-baseline justify-between gap-3" data-id="element-1036" data-mp="2xzdq3mmsd1ur">
                                            <div className={cn(`text-sm font-bold ${isDark ? 'text-white/90' : 'text-black/90'}`, 'text-[20px] font-[500]')} data-id="element-1037" data-mp="3603de52ei9uv">
                                              Estimated total
                                            </div>
                                            <div className={`text-2xl font-extrabold tabular-nums ${isDark ? 'text-white/90' : 'text-black/90'}`} data-id="element-1038" data-mp="30zfkteyixkyj">
                                              ${grandTotal.toLocaleString()}
                                            </div>
                                          </div>
                                        </>}
                                    </motion.div>
                                    {/* Large-order overlay — sits on top
                                     of the blurred breakdown card and
                                     tells the customer to text us for a
                                     custom quote. The Add-to-Truck CTA
                                     below morphs into "Large order —
                                     text us →" at the same threshold. */}
                                    {isLargeOrder && <motion.div initial={{
                          opacity: 0,
                          y: 4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.25
                        }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none" data-id="element-1039" data-mp="1yo5zx1wavt5k">
                                        <p className="text-base font-extrabold text-[#DD4132] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" data-id="element-1040" data-mp="2he5lvfwugs4r">
                                          Custom pricing for {loadCount}+ loads
                                        </p>
                                        <p className="text-xs text-white/90 mt-1 leading-snug drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]" data-id="element-1041" data-mp="2mejlqaoakvrp">
                                          Volume discounts apply — text us for
                                          your exact quote.
                                        </p>
                                      </motion.div>}
                                  </div>;
                    })()}

                            {/* Large-order handoff — the primary CTA
                             below morphs into amber "Text us" when +
                             has pushed past 4 loads. */}

                            {loadCount > 1 && !showWarning && <div className="flex items-start gap-2 p-3 bg-[#059F40]/10 border border-[#059F40]/20 rounded-none" data-id="element-1042" data-mp="27rrch1fehlix">
                                <Info className="w-4 h-4 text-[#059F40] mt-0.5 shrink-0" data-id="element-1043" data-mp="2gquyad5ycf14" />
                                <p className={`text-xs font-semibold ${isDark ? 'text-stone-100' : 'text-stone-900'}`} data-id="element-1044" data-mp="he0krwjdpuza">
                                  Multi-load orders require separate deliveries.
                                  You will be charged a delivery fee per load.
                                </p>
                              </div>}
                          </div>;
                })()}
                    </motion.div>}
                </AnimatePresence>

                {/* Submit button — only when address + material + quantity are ready */}
                <AnimatePresence data-id="element-1045" data-mp="23gxf7d84eo">
                  {showMaterialRow && material && quantity && (quantity !== 'multi' || numLoads) && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-1046" data-mp="xpkjleaiv4xo">
                        {/* Same-day countdown — only renders Mon–Fri
                         before the 12 PM cutoff (returns null otherwise).
                         Sits directly above the Add-to-Truck CTA so the
                         "Get it today" nudge is visible right where the
                         customer commits. */}
                        <div className="mb-4 flex justify-center" data-id="element-1047" data-mp="yhbzz407d7uh">
                          <SameDayBadge variant="full" data-id="element-1048" data-mp="yiwxn2htd6wk" />
                        </div>
                        {quantity === 'multi' && parseInt(numLoads, 10) >= 5 ? <button type="button" onClick={() => {
                  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                  if (!isMobile) {
                    setIsQuoteModalOpen(true);
                    return;
                  }
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const loads = parseInt(numLoads, 10) || 5;
                  const yds = loads * fullLoad;
                  const parts = [`Hi 206Gravel — I need a custom quote for ${loads} loads of ${material || 'material'} (${yds} yd).`];
                  if (address) parts.push(`Delivering to: ${address}.`);
                  parts.push('Thanks!');
                  const body = parts.join(' ');
                  window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                }} className="w-full px-6 py-5 sm:py-6 min-h-[92px] sm:min-h-[106px] rounded-2xl font-bold text-lg transition-colors flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-1049" data-mp="1r57uc22auno3">
                            <MessageSquare className="w-5 h-5 mr-2" data-id="element-1050" data-mp="2a9thl5kz3362" />
                            Large order — text us →
                          </button> : (() => {
                  // Compute live grand total at the button so
                  // the label can show "Add to Truck — $XXX".
                  // Mirrors the math in the live price window
                  // above; the two stay in sync because both
                  // use getSmallLoadSurcharge + addressZone.fee
                  // × loadCount.
                  const productInfo = productMap[material];
                  const light = isLightweight(material);
                  const fullLoad = light ? 15 : 12;
                  const yards = quantity === 'multi' ? (parseInt(numLoads, 10) || 2) * fullLoad : parseInt(quantity, 10) || fullLoad;
                  const loads = yards > fullLoad ? Math.round(yards / fullLoad) : 1;
                  const basePrice = productInfo?.pricePerYard ?? 0;
                  const surcharge = getSmallLoadSurcharge(yards, material);
                  const effectivePerYd = Math.ceil(basePrice * surcharge);
                  const materialsSubtotal = effectivePerYd * yards;
                  const zoneReady = addressZone && addressZone.zone !== null;
                  const deliveryTotal = zoneReady ? (loads - 1) * addressZone!.fee + (yards - (loads - 1) * fullLoad < 3 ? 250 : addressZone!.fee) : 0;
                  const grandTotal = materialsSubtotal + deliveryTotal;
                  const handleAddToTruckDirect = () => {
                    if (!productInfo || !zoneReady) return;
                    // Persist delivery info so the cart drawer
                    // shows the right zone/address from the
                    // moment it opens. setDeliveryInfo is a
                    // POSITIONAL setter: (address, fee, zone)
                    // — passing an object here silently stuffs
                    // the whole object into the address slot
                    // and later .trim() calls on it explode.
                    setDeliveryInfo(address, addressZone!.fee, addressZone!.zone);
                    const items = buildDeliveryLineItems(yards, material);
                    items.forEach(it => {
                      addToCart({
                        category: productInfo.categoryName,
                        product: it.product,
                        quantity: it.quantity,
                        pricePerYard: productInfo.pricePerYard,
                        source: 'estimator',
                        requestedQuantity: it.requestedQuantity
                      });
                    });
                    openCart();
                  };
                  // ── Easter-egg slider ──────────────────────────
                  // Turn the CTA into a hidden yardage slider. A
                  // horizontal drag maps the pointer's x-position
                  // across the button to a yardage in [3, fullLoad]
                  // and writes it straight into the quantity state
                  // (single-load range only). Movement past a small
                  // threshold flags the gesture as a slide so the
                  // click handler swallows the tap instead of adding
                  // to the cart.
                  const applyTruckSlide = (clientX: number, el: HTMLElement) => {
                    const rect = el.getBoundingClientRect();
                    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
                    const yds = Math.round(3 + frac * (fullLoad - 3));
                    setQuantity(String(yds));
                    setNumLoads('');
                    setCustomQty(null);
                  };
                  const handleTruckPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
                    truckDragRef.current = {
                      active: true,
                      moved: false,
                      startX: e.clientX,
                      pointerId: e.pointerId
                    };
                    try {
                      e.currentTarget.setPointerCapture(e.pointerId);
                    } catch {}
                  };
                  const handleTruckPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
                    const d = truckDragRef.current;
                    if (!d.active) return;
                    if (Math.abs(e.clientX - d.startX) > 6) {
                      if (!d.moved) setTruckSliding(true);
                      d.moved = true;
                    }
                    if (d.moved) applyTruckSlide(e.clientX, e.currentTarget);
                  };
                  const handleTruckPointerEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
                    const d = truckDragRef.current;
                    d.active = false;
                    try {
                      e.currentTarget.releasePointerCapture(d.pointerId);
                    } catch {}
                    // Let the fill settle back to its eased animation
                    // once the drag ends.
                    setTruckSliding(false);
                  };
                  // "Load bar" effect — the bright green
                  // fill animates from full (at one truckload)
                  // down toward ~25% (at the 3 yd minimum),
                  // revealing a darker green underneath. Above
                  // a full load (multi-delivery) it stays at
                  // 100%. Visualizes the truck filling up as
                  // the customer increases yardage.
                  const loadPct = Math.min(100, Math.max(0, yards / fullLoad * 100));
                  // On a fresh material selection the bright fill
                  // shouldn't snap to the full-load width — it should
                  // appear already mostly full (at the 10yd mark) and
                  // then ease the remaining 10yd → full-load sliver in,
                  // decelerating so the final ~0.5yd creeps in slowly.
                  const revealStartPct = Math.min(loadPct, 10 / fullLoad * 100);
                  return <button type="button" onClick={e => {
                    // Swallow the tap if this was actually a
                    // slide (Easter-egg yardage drag). A plain
                    // tap falls through to add-to-cart.
                    if (truckDragRef.current.moved) {
                      truckDragRef.current.moved = false;
                      e.preventDefault();
                      return;
                    }
                    handleAddToTruckDirect();
                  }} onPointerDown={handleTruckPointerDown} onPointerMove={handleTruckPointerMove} onPointerUp={handleTruckPointerEnd} onPointerCancel={handleTruckPointerEnd} disabled={!address || !zoneReady} style={{
                    touchAction: 'pan-y'
                  }} className={cn('relative w-full overflow-hidden text-white px-6 py-4 sm:py-5 min-h-[83px] sm:min-h-[95px] rounded-none font-bold text-lg flex items-center justify-center transition-colors disabled:opacity-70 shadow-[0_0_20px_rgba(5,159,64,0.3)] select-none',
                  // Base layer (UNDERNEATH the top fill) — the
                  // "peel off" green revealed as the bar recedes.
                  // Starbucks green desaturated 50%.
                  'bg-[#1f5744]', 'bg-[#007B46]')} data-id="element-1051" data-mp="3fm08z4p2ev4g">
                                {/* Two-layer "load bar": a DARKER green base
                                 (#036c2c) sits behind a bright #059F40 fill
                                 whose width tracks how full the truck is
                                 (loadPct). As the customer reduces quantity,
                                 the bright fill recedes and the darker green
                                 base shows through on the right — visualizing
                                 the truck emptying. Square corners, white
                                 label, identical in light and dark modes. */}
                                <motion.span key={loadBarRevealKey} aria-hidden="true" className={cn('absolute inset-y-0 left-0 z-0',
                    // Top fill — the primary button green:
                    // Starbucks green (#00915F) brightened ~1.5
                    // shades, sitting over the desaturated
                    // peel-off base. In dark mode it's dropped
                    // ~1 shade darker (#00754A) so it doesn't
                    // glow against the dark card.
                    isDark ? 'bg-[#00754A]' : 'bg-[#006942]', 'bg-[#006942]')}
                    // Keying on loadBarRevealKey re-mounts this on
                    // each material pick so it animates FROM the
                    // 10yd mark up to the default full-load width.
                    // +/- quantity changes (no key change) animate
                    // from the current width instead.
                    initial={{
                      width: `${revealStartPct}%`
                    }} animate={{
                      width: `${loadPct}%`
                    }} transition={truckSliding ? {
                      // While the Easter-egg slide is
                      // active, the fill tracks the finger
                      // instantly instead of easing.
                      duration: 0
                    } : {
                      // Decelerating ease-out so the fill
                      // rushes in then slows hard at the end
                      // — the last sliver (final ~0.5yd)
                      // creeps in slowly.
                      duration: 3.2,
                      ease: [0.16, 1, 0.3, 1]
                    }} data-id="element-1052" data-mp="2apapk4ql2mt1" />
                                <span className="relative z-10 flex items-center justify-center" data-id="element-1053" data-mp="2r2eqhfkp6r7c">
                                  {zoneReady ? <>
                                      Add to Truck — $
                                      {grandTotal.toLocaleString()}
                                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1054" data-mp="30109fbu3088n" />
                                    </> : <>
                                      Enter your address to continue
                                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1055" data-mp="15qwmosmozciy" />
                                    </>}
                                </span>
                              </button>;
                })()}
                        {/* Secondary actions — plain text links (no pill
                         chrome) so the primary Add-to-Truck CTA stays the
                         clear focus. Keeps the phone-in and product-detail
                         paths available without competing button visuals. */}
                        {productMap[material] && <div className="mt-3 pt-2.5 flex items-center justify-center gap-5 text-[17px]" data-id="element-1056" data-mp="1d85xj5a3joj7">
                            <a href="tel:206-619-3742" className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-stone-700 hover:text-stone-900'}`} data-id="element-1057" data-mp="1i5y1wyrxh34a">
                              <Phone className="w-5 h-5 text-[#059F40]" data-id="element-1058" data-mp="1s3ly7t56miok" />
                              Call to Order
                            </a>
                            <span className={isDark ? 'text-white/20' : 'text-stone-300'} aria-hidden="true" data-id="element-1059" data-mp="3twa4ych1bo93">
                              |
                            </span>
                            <Link to={`/shop/${productMap[material].slug}?qty=${encodeURIComponent(quantity === 'multi' ? String((parseInt(numLoads, 10) || 2) * (isLightweight(material) ? 15 : 12)) : quantity)}`} className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-stone-700 hover:text-stone-900'}`} data-id="element-1060" data-mp="2b39y5kilbpqz">
                              <FileText className="w-5 h-5 text-[#059F40]" data-id="element-1061" data-mp="2tzhw0399wyc9" />
                              View Material
                            </Link>
                          </div>}
                      </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex flex-row flex-nowrap items-center justify-center gap-2 sm:gap-4 text-[11px] sm:text-xs text-stone-700" data-id="element-1062" data-mp="2g11q2ej8jbv6">
                <div className="flex items-center whitespace-nowrap text-[#888888]" data-id="element-1063" data-mp="19lhi18r8qzih">
                  <Lock className="w-3 h-3 mr-1.5 shrink-0 opacity-70" data-id="element-1064" data-mp="14jii68upmkqa" />
                  Your information is secure
                </div>
                <span className={isDark ? 'text-white/20' : 'text-stone-400'} aria-hidden="true" data-id="element-1065" data-mp="1b1d6ejmh2q4j">
                  |
                </span>
                {(() => {
              // Compute current Pacific time + day. Dispatch hours:
              // Mon–Fri 5am–6pm, Sat 6am–3pm, Sun closed.
              const nowPT = new Date(new Date().toLocaleString('en-US', {
                timeZone: 'America/Los_Angeles'
              }));
              const day = nowPT.getDay(); // 0=Sun, 6=Sat
              const hour = nowPT.getHours();
              let isActive = false;
              let label = '';
              if (day === 0) {
                // Sunday — closed all day
                label = 'Dispatch Closed Sundays';
              } else if (day === 6) {
                isActive = hour >= 6 && hour < 15;
                label = isActive ? 'Dispatch Active' : 'Dispatch Opens 6 AM';
              } else {
                isActive = hour >= 5 && hour < 18;
                label = isActive ? 'Dispatch Active' : day === 5 && hour >= 18 ? 'Dispatch Opens Sat 6 AM' : 'Dispatch Opens 5 AM';
              }
              return <div className={cn('flex items-center gap-1.5 whitespace-nowrap font-medium', isActive ? 'text-[#00B774]' : 'text-[#888888]')} data-id="element-1066" data-mp="3vu1rc8851jkd">
                      <span className="relative flex h-2 w-2 shrink-0" data-id="element-1067" data-mp="3qwrtc46onzkx">
                        {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059F40] opacity-75" data-id="element-1068" data-mp="8nhf4a93goux"></span>}
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-[#059F40]' : 'bg-[#888888]'}`} data-id="element-1069" data-mp="ievmt5b78uz5"></span>
                      </span>
                      {label}
                    </div>;
            })()}
              </div>

              {/* Quote Result — DEPRECATED. Live price window + Add to
               Truck CTA above replace this post-submit interstitial.
               Kept as dead code (false &&) so the supporting state /
               refs / handlers stay valid and we have a quick rollback
               path if needed. */}
              {false && quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-stone-300/40 dark:border-white/10" data-id="element-1070" data-mp="2qmic1owq6cyx">
                  <p className="text-xs text-stone-700 dark:text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-1071" data-mp="1xzntixjgxno3">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-stone-800 dark:text-white/90 font-medium mb-4" data-id="element-1072" data-mp="27uy100smyn99">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-1073" data-mp="3jb64c4fw62lz">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-1074" data-mp="1bpebay6azt2d">
                    {/* Per-delivery breakdown for multi-load orders —
                     mirrors the Services hub and Product page. Each
                     delivery gets its own yards + dollar row so the
                     "rate × loads = total" math is fully transparent.
                     Single-load orders fall back to the compact
                     "Material — $X/yd × Y yds" row. */}
                    {quoteResult.deliveries && quoteResult.deliveries > 1 ? (() => {
                const totalYards = parseInt(quoteResult.quantity) || 0;
                const count = quoteResult.deliveries;
                const fullLoad = Math.floor(totalYards / count);
                const remainder = totalYards - fullLoad * count;
                // Distribute remainder across leading loads so
                // we don't end up with a stray 0-yard "last
                // load" — keeps the split intuitive when
                // quantity doesn't divide evenly.
                const perDelivery: number[] = [];
                for (let i = 0; i < count; i++) {
                  perDelivery.push(fullLoad + (i < remainder ? 1 : 0));
                }
                // If the calculator flagged a min-load
                // adjustment, the last load is BILLED higher
                // than its actual yardage. Surface that on
                // the last row.
                const lastActual = quoteResult.minLoadAdjustment?.lastLoadActual;
                return <>
                            {perDelivery.map((loadYards, idx) => {
                    const isLast = idx === perDelivery.length - 1;
                    const showCharged = isLast && typeof lastActual === 'number' && lastActual < loadYards;
                    const dollars = quoteResult.effectivePricePerYardLow * loadYards;
                    return <div key={idx} className="flex justify-between text-sm" data-id="element-1075" data-mp="1quiheb06tmzc" data-mpi={idx}>
                                  <span className="text-stone-700 dark:text-white/70" data-id="element-1076" data-mp="1vstrmmue9zi3">
                                    Delivery {idx + 1}: {loadYards} yds
                                    {showCharged && ` (charged as ${loadYards})`}
                                  </span>
                                  <span className="text-stone-900 dark:text-white font-semibold" data-id="element-1077" data-mp="1x7eey5nkewgq">
                                    ${dollars.toLocaleString()}
                                  </span>
                                </div>;
                  })}
                            <p className="text-xs text-brand-orange" data-id="element-1078" data-mp="10ioviawahvsn">
                              + additional delivery fee for{' '}
                              {perDelivery.length - 1} extra{' '}
                              {perDelivery.length > 2 ? 'loads' : 'load'}
                            </p>
                          </>;
              })() : <div className="flex justify-between text-sm" data-id="element-1079" data-mp="lshcnu2dearf">
                        <span className="text-stone-700 dark:text-white/70" data-id="element-1080" data-mp="guplj29jusoa">
                          Material — ${quoteResult.effectivePricePerYardLow}
                          /yd × {quoteResult.quantity} yds
                        </span>
                        <span className="text-stone-900 dark:text-white font-semibold" data-id="element-1081" data-mp="apscw9uocoh">
                          $
                          {quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}
                        </span>
                      </div>}
                    {quoteResult.minLoadAdjustment && <div className="-mt-1 ml-0.5" data-id="element-1082" data-mp="f785wo5fb8qr">
                        <button type="button" onClick={() => setShowMinLoadInfo(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={showMinLoadInfo} data-id="element-1083" data-mp="j8jx2y3y4212">
                          <Info className="w-3.5 h-3.5" data-id="element-1084" data-mp="sydcka77l1al" />
                          You asked for{' '}
                          {quoteResult.minLoadAdjustment.requested} yds — billed
                          for {quoteResult.minLoadAdjustment.billable} yds. Why?
                        </button>
                        <AnimatePresence data-id="element-1085" data-mp="2ngoe9mir13ff">
                          {showMinLoadInfo && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-1086" data-mp="2ifh4jhx1pu64">
                              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-1087" data-mp="317t8g93cl3id">
                                <p data-id="element-1088" data-mp="2rhgnwgvj9ac8">
                                  <span className="font-bold" data-id="element-1089" data-mp="2rgf1xhczb3fq">
                                    3-yard order minimum.
                                  </span>{' '}
                                  You asked for{' '}
                                  {quoteResult.minLoadAdjustment.requested} yd —
                                  billed as 3 yd.
                                </p>
                                <p data-id="element-1090" data-mp="krbrcodaubva">
                                  Trucks have fixed dispatch costs, so any
                                  single delivery is billed at 3 yds even if you
                                  only need a couple. Round up to fill the truck
                                  and you'll get more material at no extra cost.
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}
                    <SmallLoadBreakdown quantity={parseInt(quoteResult.quantity) || 0} productName={quoteResult.material} pricePerYard={quoteResult.basePricePerYardLow} className="-mt-1 ml-0.5" data-id="element-1091" data-mp="1ifc4jj3s0yia" />
                    {/* Green "X% bulk discount applied" line removed to
                     match Product and Services — the discount is
                     reflected in the per-delivery rows above and the
                     Estimated Total below. */}
                    <div className="flex justify-between text-sm" data-id="element-1092" data-mp="2ayy329oavzgn">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-1093" data-mp="1x0huseasi0we">
                        Delivery —{' '}
                        {addressUnverified ? 'Confirmed by dispatch' : quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className={`font-semibold ${addressUnverified ? 'text-amber-400' : 'text-stone-900 dark:text-white'}`} data-id="element-1094" data-mp="1dd0go16sv9zf">
                        {addressUnverified ? 'TBD' : quoteResult.deliveryZone !== null ? `${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-stone-300/40 dark:border-white/10" data-id="element-1095" data-mp="199c5z9mvfy9y">
                      <span className="text-stone-900 dark:text-white font-bold text-sm" data-id="element-1096" data-mp="3b1w3x9hvjbi6">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-stone-900 dark:text-white" data-id="element-1097" data-mp="31b1gw2wt07vo">
                        ${quoteResult.priceLow}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-center" data-id="element-1098" data-mp="3txvxgoqi2n3v">
                    <SameDayBadge variant="full" data-id="element-1099" data-mp="3p0nfao5cqj2m" />
                  </div>

                  <div className="flex flex-col gap-3" data-id="element-1100" data-mp="3kxqs0k4v9ysc">
                    <button type="button" onClick={handleAddToTruckFromQuote} className="w-full bg-[#059F40] hover:bg-[#059F40] text-white px-6 py-[18px] rounded-lg font-bold text-lg transition-colors flex items-center justify-center shadow-[0_0_20px_rgba(5,159,64,0.25)]" data-id="element-1101" data-mp="3kw5xuvmgshv0">
                      Add to Truck & Check Out
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1102" data-mp="24m3kkg9eo1we" />
                    </button>
                    <div className={`grid ${quoteResult.materialSlug ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5`} data-id="element-1103" data-mp="jzlrwdcdlb4l">
                      <a href="tel:206-619-3742" className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1104" data-mp="t2eh8b1zzcrs">
                        <Phone className="w-4 h-4 text-[#059F40]" data-id="element-1105" data-mp="17pprm9mg0qqb" />
                        Call to Order
                      </a>
                      {quoteResult.materialSlug && <Link to={`/shop/${quoteResult.materialSlug}?qty=${encodeURIComponent(
                // Pass the user's REQUESTED yardage (e.g. 14), not
                // the billable value (17 after 5yd-min adjustment).
                // The Product page re-applies the split via
                // buildDeliveryLineItems so the cart shows the same
                // 12 + 5 breakdown the estimator quoted.
                String(quoteResult.minLoadAdjustment?.requested ?? quoteResult.quantity))}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#059F40]/50 dark:hover:border-[#059F40]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-1106" data-mp="1gsh7qmq9kaoa">
                          <FileText className="w-4 h-4 text-[#059F40]" data-id="element-1107" data-mp="1zkmbyqnytuyx" />
                          View Material
                        </Link>}
                    </div>
                  </div>

                  {/* Delivery Zone Note */}
                  {addressUnverified ? <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-400/10 p-3.5" data-id="element-1108" data-mp="1ls6f67lnqt6g">
                      <div className="flex items-start gap-2.5" data-id="element-1109" data-mp="1gtd22gaa946t">
                        <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-1110" data-mp="1gstkx1sgijtu" />
                        <div className="flex-1 min-w-0 text-left" data-id="element-1111" data-mp="3sevq62gfkg0r">
                          <p className="text-sm font-bold text-amber-200 leading-snug" data-id="element-1112" data-mp="1211xv15uqc2">
                            We'll confirm your address before dispatch
                          </p>
                          <p className="mt-1 text-xs text-white/80 leading-relaxed" data-id="element-1113" data-mp="3ngu7himx2ipt">
                            This estimate covers the materials only. Once you
                            place the order, dispatch will call you to verify
                            the delivery address and quote the final delivery
                            fee — usually within one business hour.
                          </p>
                          <a href="tel:206-619-3742" className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#22c95c] hover:text-white transition-colors" data-id="element-1114" data-mp="az5nptr3gjsc">
                            <Phone className="w-3 h-3" data-id="element-1115" data-mp="2vpwhwnfuje8k" />
                            Prefer to call now? (206) 619-3742
                          </a>
                        </div>
                      </div>
                    </div> : quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-stone-700 dark:text-white/70 leading-relaxed text-center font-medium" data-id="element-1116" data-mp="wbeg3yylcutp">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="text-[#059F40] hover:underline" data-id="element-1117" data-mp="nhhviol2rqge">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-[#059F40]" data-id="element-1118" data-mp="djli1q4o0jf4">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-1119" data-mp="8lsgxbi7yflp" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?${estimatorReturnParams}`} className="font-semibold ml-1 hover:underline" data-id="element-1120" data-mp="22y9yq3kagniy">
                        check delivery zones
                      </Link>
                    </div>}
                </motion.div>}

              {/* Restart — only appears once the customer has gone through the
               flow far enough that a price is showing (material selected and
               priced). Before that there's nothing to start over. Icon-only:
               the "Start over" label is dropped, the RotateCcw glyph carries
               the meaning (and the aria-label keeps it accessible). Resets to
               a fresh estimator and re-anchors the live ticker. */}
              {showMaterialRow && material && productMap[material] && <div className="mt-4 flex justify-end" data-id="element-1121" data-mp="36xewnpikuwpv">
                  <button type="button" onClick={() => {
              handleCloseEstimator();
              setShowEstimator(true);
              setTimeout(slideToTicker, 200);
            }} className={`inline-flex items-center justify-center transition-colors p-2 -mr-2 rounded ${isDark ? 'text-white/40 hover:text-white/70' : 'text-stone-500 hover:text-stone-800'}`} aria-label="Start over" title="Start over" data-id="element-1122" data-mp="36vu2i106dfsj">
                    <RotateCcw className="w-4 h-4" data-id="element-1123" data-mp="2r2wyv08w86q" />
                  </button>
                </div>}
            </motion.div>}
        </AnimatePresence>

        {/* Catalog escape hatch — sits below the open estimator so the
            browse path stays discoverable without competing with the form. */}
        <AnimatePresence data-id="element-1124" data-mp="6t7nvlzb6qpo">
          {showEstimator && <motion.div key="catalog-link" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} className="-mt-[10px] mb-8 max-w-[800px] mx-auto md:mx-0" data-id="element-1125" data-mp="lj5hihg0c8m9">
              <ShopByProjectCTA estimator isDark={isDark} data-id="element-1126" data-mp="qjjf15jzg061" />
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}

        <div className="mt-10" ref={tickerRef} data-id="element-1127" data-mp="2saegacwykrk5">
          <LiveDeliveryTicker isDark={isDark} onPhoto={!showEstimator} data-id="element-1128" data-mp="2sbpqovkwsxk8" />
        </div>
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1129" data-mp="2i7r5iueg1mm8" />
    </section>;
}
export default Hero;